import { api } from '../shared/services/api';
import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner, PageFooter } from '../shared/components';
import { icon } from '../shared/components/icons';
import { filterBar } from '../shared/lib/tableControls';
import Chart from 'chart.js/auto';

// ── Interfaces ────────────────────────────────────────────────────────────────

interface QBPropertyRaw {
  name: string;
  classGroup: string;
  income: number;
  cogs: number;
  grossProfit: number;
  expenses: number;
  netIncome: number;
  cleaningIncome: number;
  cleaningCosts: number;
  damageWaiverIncome: number;
}

interface QBPropertyComputed extends QBPropertyRaw {
  reservations: number;
  isMapped: boolean;
  profitMargin: number;
  expenseRatio: number;
  allocatedOverhead: number;
  effectiveMargin: number;
}

interface QBOverheadItem {
  rowLabel: string;
  amount: number;
}

// One row of /api/projections/summary/:year — projections from the store,
// actuals always recomputed by the live fee engine on the server.
interface ProjRow {
  month: number;
  projected_gross: number;
  projected_mgmt_fee: number;
  projected_net: number;
  actual_gross: number;
  actual_mgmt_fee: number;
  actual_net: number;
}

interface BLRFee {
  id: number;
  fee_name: string;
  price_per_occurrence: number;
  take_up_rate: number;
}

interface QBPeriod {
  id: string;
  label: string;
  uploadedAt: string;
  periodStart: string | null;  // YYYY-MM-DD parsed from CSV
  periodEnd: string | null;
  rawProps: QBPropertyRaw[];
  overheadItems: QBOverheadItem[];
  notSpecifiedProps: QBPropertyRaw[];
}

const QB_STORAGE_KEY = 'surge_qb_periods';
const QB_MAP_KEY     = 'surge_qb_listing_map'; // normalizedQBName → listing_id

const EXCLUDED_CLASSES = new Set(['coo', 'executive']);
const AGGREGATE_COLS = new Set([
  '0 - overhead', '1 - managed', '2 - arbitrage', '3 - owned', '4 - mmb',
  'executive', 'not specified', 'total', 'unspecified', 'overhead', 'coo',
]);

function isAggCol(name: string): boolean {
  const l = name.toLowerCase().trim();
  return AGGREGATE_COLS.has(l) || l.startsWith('total for') || l === '';
}

// Property types drive profitability attribution server-side: 'inactive' is
// excluded from margins entirely; 'mtr' and 'owner_occupied' get reduced overhead.
const PROPERTY_TYPE_META: { value: string; label: string; color: string; note: string }[] = [
  { value: 'standard',       label: 'Standard',       color: '#3b82f6', note: 'Full overhead allocation' },
  { value: 'mtr',            label: 'MTR',            color: '#8b5cf6', note: 'Mid-term rental — reduced overhead (×0.6)' },
  { value: 'owner_occupied', label: 'Owner-occupied', color: '#16a34a', note: 'Reduced overhead (×0.2)' },
  { value: 'inactive',       label: 'Inactive',       color: '#6b7280', note: 'Excluded from margin analysis' },
];
function typeMeta(t: string) {
  return PROPERTY_TYPE_META.find(m => m.value === t) || PROPERTY_TYPE_META[0];
}

// ── Page ──────────────────────────────────────────────────────────────────────

export class ProfitabilityPage {
  private container: HTMLElement;
  private data: any = null;
  private sortField: string = 'gor';
  private sortDir: 'asc' | 'desc' = 'desc';
  private qbChartInstance: Chart | null = null;
  private trendChartInstance: Chart | null = null;

  // Monthly P&L statement + config-strip projections editor (shared state)
  private readonly projYear: number = new Date().getFullYear();
  private projRows: ProjRow[] = [];
  private projLoaded = false;
  // BLR config strip
  private blrFees: BLRFee[] = [];
  // Revenue breakdown — listing_id → active formula assignment
  private assignmentByListing = new Map<string, any>();

  // QB multi-period state
  private qbPeriods: QBPeriod[] = [];
  private qbActivePeriodIdx: number = 0;
  // normalizedQBName → reservation count for the active period
  private qbResCounts: Record<string, number> = {};
  private qbResCountsLoaded = false;
  // normalizedQBName → Supabase listing_id (persisted in localStorage)
  private qbListingMap: Record<string, number> = {};
  // listing_id → count (populated by fetchPeriodReservationCounts)
  private listingIdToCount: Record<number, number> = {};
  // all Supabase listings for the mapping dropdown + channel links
  private allListings: { id: string; name: string; short_name?: string; airbnb_url?: string; vrbo_url?: string; expedia_url?: string; google_vr_url?: string; wch_url?: string }[] = [];
  // listing_id → listing (for O(1) lookup in renderQBTable)
  private listingById: Map<string, any> = new Map();
  // qbNorm → resolved Supabase listing UUID (auto-matched + manual)
  private qbNormToListingId: Record<string, string> = {};

  // QB filter state
  private qbSearch: string = '';
  private qbFilterClass: string = '';
  private qbFilterProfit: string = 'all';
  private qbMinRevenue: number = 0;
  private qbSortField: keyof QBPropertyComputed = 'netIncome';
  private qbSortDir: 'asc' | 'desc' = 'desc';
  private qbPage: number = 1;
  private readonly qbPerPage: number = 20;

  // Live leaderboard
  private leaderPage: number = 1;
  private readonly leaderPerPage: number = 25;
  // Live leaderboard client-side filters
  private leaderSearch: string = '';
  private leaderType: string = 'all'; // all | standard | mtr | owner_occupied | inactive
  // listing_id → property type (standard|mtr|inactive|owner_occupied)
  private propertyTypes: Record<string, string> = {};
  // listing_id of the leaderboard row currently expanded into a detail drawer
  private expandedRowId: string | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  // Guarded money string — never renders $NaN/$undefined.
  private static money(n: number | null | undefined): string {
    return Number.isFinite(n as number) ? `$${Formatters.formatNumber(n)}` : '—';
  }
  // Guarded percent string — never renders NaN%.
  private static pct(n: number | null | undefined, decimals = 1): string {
    return Number.isFinite(n as number) ? `${(n as number).toFixed(decimals)}%` : '—';
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <style>
        /* P&L tab — flat accounting layout: no hero gradients, no dark strips. */
        .pnl-band { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); border: 1px solid var(--border); border-radius: var(--radius); background: #fff; overflow: hidden; margin-bottom: 20px; }
        .pnl-cell { padding: 14px 18px; border-right: 1px solid var(--border); min-width: 0; background: #fff; }
        .pnl-cell:last-child { border-right: none; }
        .pnl-eyebrow { font-size: 10px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
        .pnl-num { font-size: 22px; font-weight: 600; margin-top: 4px; }
        .pnl-sub { font-size: 11px; color: var(--text-muted); margin-top: 3px; overflow-wrap: anywhere; }
        .pnl-source { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); margin: 0 2px 6px; }
        .pnl-caption { font-size: 11px; color: var(--text-muted); padding: 8px 20px 10px; }
        .pnl-config { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 16px; align-items: start; }
        #pnl-monthly-table th.num, #pnl-monthly-table td.num { text-align: right; }
        #qb-workbench > summary { cursor: pointer; padding: 16px 20px; }
        #qb-workbench > summary h3 { display: inline; font-size: 15px; margin: 0; }
        #qb-workbench > summary .pnl-summary-sub { font-size: 12px; color: var(--text-muted); margin-left: 8px; }
        @media (max-width: 1024px) {
          .pnl-band { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .pnl-cell { border-bottom: 1px solid var(--border); }
          .pnl-config { grid-template-columns: minmax(0, 1fr); }
        }
      </style>
      <div class="page-content">

        <div class="dash-head">
          <h1>Profitability</h1>
          <p>The accounting review — QuickBooks actuals where uploaded, the expense model everywhere else, and every number says which it is.</p>
        </div>

        <!-- 1 · P&L headline band -->
        <div class="kpi-section-label" id="pnl-band-source">P&amp;L headline</div>
        <div class="kpi-grid" id="pnl-band" style="margin-bottom: 12px;">
          <div style="grid-column: 1 / -1;">${LoadingSpinner.render()}</div>
        </div>
        <div id="pnl-denominator"></div>

        <!-- 2 · Monthly P&L statement -->
        <div class="table-container" style="margin-bottom: 24px;">
          <div class="table-header">
            <h3>Monthly P&amp;L statement — ${new Date().getFullYear()}</h3>
            <div style="font-size: 12px; color: var(--text-muted);">Gross &amp; mgmt rev = live engine (synced reservations; months after today = on the books so far) · projected = saved monthly plan (editable at the bottom of this page) · QuickBooks columns fill in only for months a monthly upload covers</div>
          </div>
          <div id="pnl-monthly-wrap" style="overflow-x: auto;">${LoadingSpinner.render()}</div>
        </div>

        <!-- 3 · Revenue breakdown by property (engine-true) -->
        <div class="table-container" style="margin-bottom: 24px;">
          <div class="table-header">
            <h3>Revenue breakdown by property</h3>
            <div style="font-size: 12px; color: var(--text-muted);">Engine-true · <span id="pnl-revmetrics-window">year-to-date</span> · Gross → Commissionable → Mgmt Fee → Total Company → Owner Net. Changing a formula creates a real assignment (same as Configuration) and recomputes.</div>
          </div>
          <div class="u-table-wrap">
            <table id="pnl-revmetrics-table" class="data-table" style="min-width: 720px;">
              <thead><tr>
                ${['Property','Formula','Gross','Commissionable','Mgmt Fee','Total Company','Owner Net'].map((h,i)=>`<th class="${i<=1?'':'num'}">${h}</th>`).join('')}
              </tr></thead>
              <tbody id="pnl-revmetrics"><tr><td colspan="7" style="padding:24px;">${LoadingSpinner.render()}</td></tr></tbody>
              <tfoot id="pnl-revmetrics-foot"></tfoot>
            </table>
          </div>
        </div>

        <!-- Modeled leaderboard + property-type editor (kept; GOR chart cut as redundant with Money's table) -->
        <div class="table-container" style="margin-bottom: 24px;">
          <div class="table-header">
            <h3>Property Leaderboard — modeled</h3>
            <div style="font-size: 12px; color: var(--text-muted);">All synced reservations · expenses modeled at 72% of mgmt rev × type factor · click a row for details &amp; property type</div>
          </div>
          <div id="leader-controls" style="padding: 0 16px;"></div>
          <div class="table-scroll">
            <table style="width: 100%;">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Property</th>
                  <th class="sortable" data-field="gor" style="cursor:pointer;">GOR ↕</th>
                  <th class="sortable" data-field="net_profit" style="cursor:pointer;">Net (modeled) ↕</th>
                  <th class="sortable" data-field="margin_percent" style="cursor:pointer;">Margin on mgmt rev (modeled) ↕</th>
                </tr>
              </thead>
              <tbody id="profit-table-body">
                <tr><td colspan="5">${LoadingSpinner.render()}<\/td><\/tr>
              </tbody>
              <tfoot id="profit-table-foot"></tfoot>
            </table>
          </div>
          <div class="pagination-container" style="display:flex; justify-content:space-between; align-items:center; padding:10px 16px; border-top:1px solid var(--border);">
            <div style="font-size:12px; color:var(--text-muted);">
              <span id="leader-page-start">0</span>–<span id="leader-page-end">0</span> of <span id="leader-page-total">0</span>
            </div>
            <div style="display:flex; gap:6px; align-items:center;">
              <button class="btn btn-sm btn-secondary" id="leader-prev-btn" disabled>←</button>
              <span id="leader-page-label" style="font-size:12px; color:var(--text-muted);"></span>
              <button class="btn btn-sm btn-secondary" id="leader-next-btn">→</button>
            </div>
          </div>
        </div>

        <!-- 4 · QuickBooks workbench (upload / mapping / overhead / filters / table) -->
        <div class="table-container" style="margin-bottom: 24px;">
          <details id="qb-workbench">
            <summary>
              <h3>QuickBooks workbench</h3>
              <span class="pnl-summary-sub">Upload monthly "Profit and Loss by Class" CSV exports · map properties · allocate overhead · per-property table</span>
            </summary>

          <!-- Period row -->
          <div style="padding: 14px 20px; border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-height: 56px;">
            <div id="qb-period-chips" style="display: flex; gap: 6px; flex-wrap: wrap; flex: 1; min-height: 32px; align-items: center;">
              <span style="font-size: 12px; color: var(--text-muted);">No periods loaded — upload a CSV to get started</span>
            </div>
            <label class="btn btn-secondary btn-sm" for="qb-csv-input" style="cursor: pointer; white-space: nowrap;">+ Add Period</label>
            <input type="file" id="qb-csv-input" accept=".csv" style="display: none;">
            <button class="btn btn-sm btn-secondary" id="qb-map-btn" style="display: none;">Map Properties</button>
            <button class="btn btn-sm btn-secondary" id="qb-export-btn" style="display: none;">Export CSV</button>
            <button class="btn btn-sm btn-danger" id="qb-clear-btn" style="display: none;">✕ Clear All</button>
          </div>

          <!-- Not Specified Warning -->
          <div id="qb-not-specified" style="display: none; margin: 12px 20px 0; padding: 10px 14px; background: #fef9c3; border: 1px solid #fde047; border-radius: 6px; font-size: 12px; color: #713f12;">
            <strong>⚠ Not Specified class:</strong> <span id="qb-ns-count">0</span> properties excluded from calculations.
            <button id="qb-ns-toggle" style="margin-left: 8px; background: none; border: none; cursor: pointer; font-size: 12px; color: #713f12; text-decoration: underline;">Show</button>
            <div id="qb-ns-detail" style="display: none; margin-top: 8px; font-size: 11px;"></div>
          </div>

          <!-- Trend Chart (2+ periods) -->
          <div id="qb-trend-container" style="display: none; padding: 20px; border-bottom: 1px solid var(--border);">
            <div style="font-size: 13px; font-weight: 600; margin-bottom: 12px; color: var(--text-muted);">Portfolio Trend — Profit Margin &amp; Net Income</div>
            <canvas id="qb-trend-chart" height="80"></canvas>
          </div>

          <!-- Overhead Config Panel -->
          <div id="qb-overhead-panel" style="display: none; padding: 14px 20px; border-bottom: 1px solid var(--border); background: var(--bg-secondary, #f8fafc);">
            <div style="font-size: 13px; font-weight: 600; margin-bottom: 10px;">Overhead Allocation
              <span style="font-size: 11px; font-weight: 400; color: var(--text-muted); margin-left: 8px;">Configure how each overhead cost is distributed across properties</span>
            </div>
            <div id="qb-overhead-items" style="display: flex; flex-direction: column; gap: 6px;"></div>
          </div>

          <!-- Property Mapping Panel -->
          <div id="qb-mapping-panel" style="display: none; padding: 14px 20px; border-bottom: 1px solid var(--border); background: #fffbeb;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <div>
                <span style="font-size: 13px; font-weight: 600;">Property Mapping</span>
                <span style="font-size: 11px; color: var(--text-muted); margin-left: 8px;">Link QB property names to Supabase listings for reservation counts. Saved permanently.</span>
              </div>
              <div style="display: flex; gap: 8px;">
                <button class="btn btn-sm btn-primary" id="qb-save-mapping-btn">Save Mapping</button>
                <button class="btn btn-sm btn-secondary" id="qb-close-mapping-btn">Close</button>
              </div>
            </div>
            <div id="qb-mapping-status" style="font-size: 12px; color: var(--text-muted); margin-bottom: 8px;"></div>
            <div style="max-height: 380px; overflow-y: auto; border: 1px solid var(--border); border-radius: 6px;">
              <table style="width: 100%; font-size: 12px;">
                <thead>
                  <tr style="background: var(--bg-secondary, #f8fafc);">
                    <th style="padding: 8px 12px; text-align: left; font-weight: 600; width: 40%;">QB Property Name</th>
                    <th style="padding: 8px 12px; text-align: left; font-weight: 600;">Supabase Listing</th>
                  </tr>
                </thead>
                <tbody id="qb-mapping-rows"></tbody>
              </table>
            </div>
          </div>

          <!-- Filters -->
          <div id="qb-filters" style="display: none; padding: 12px 20px; background: var(--bg-secondary, #f8fafc); border-bottom: 1px solid var(--border); gap: 12px; flex-wrap: wrap; align-items: flex-end;">
            <div class="filter-group" style="flex: 1; min-width: 160px;">
              <label style="display: block; font-size: 12px; margin-bottom: 4px; color: var(--text-muted);">Search</label>
              <input type="text" id="qb-search" placeholder="Search properties..." style="width: 100%; padding: 6px 10px; border: 1px solid var(--border); border-radius: 6px; font-size: 13px;">
            </div>
            <div class="filter-group" style="min-width: 140px;">
              <label style="display: block; font-size: 12px; margin-bottom: 4px; color: var(--text-muted);">Class</label>
              <select id="qb-class-filter" style="width: 100%; padding: 6px 10px; border: 1px solid var(--border); border-radius: 6px; font-size: 13px;">
                <option value="">All Classes</option>
              </select>
            </div>
            <div class="filter-group" style="min-width: 120px;">
              <label style="display: block; font-size: 12px; margin-bottom: 4px; color: var(--text-muted);">Min Revenue</label>
              <input type="number" id="qb-min-revenue" placeholder="0" min="0" step="100" style="width: 100%; padding: 6px 10px; border: 1px solid var(--border); border-radius: 6px; font-size: 13px;">
            </div>
            <div class="filter-group">
              <label style="display: block; font-size: 12px; margin-bottom: 4px; color: var(--text-muted);">Profitability</label>
              <div style="display: flex; gap: 4px;">
                <button class="qb-profit-btn btn btn-sm btn-primary" data-val="all">All</button>
                <button class="qb-profit-btn btn btn-sm btn-secondary" data-val="profitable">✅ Profit</button>
                <button class="qb-profit-btn btn btn-sm btn-secondary" data-val="loss">🔴 Loss</button>
              </div>
            </div>
            <div style="font-size: 12px; color: var(--text-muted); margin-left: auto; align-self: center;">
              <strong id="qb-filtered-count">0</strong> / <strong id="qb-total-count">0</strong> properties
            </div>
          </div>

          <!-- Revenue Composition Chart -->
          <div id="qb-chart-container" style="display: none; padding: 20px;">
            <canvas id="qb-chart"></canvas>
          </div>

          <!-- Property Table -->
          <div id="qb-table-container" style="display: none;">
            <div style="overflow-x: auto;">
              <table style="min-width: 1050px;">
                <thead>
                  <tr>
                    <th class="qb-sort" data-field="name" style="cursor: pointer; white-space: nowrap;">Property ↕</th>
                    <th class="qb-sort" data-field="classGroup" style="cursor: pointer; white-space: nowrap;">Class ↕</th>
                    <th class="qb-sort" data-field="income" style="cursor: pointer; text-align: right; white-space: nowrap;">Revenue ↕</th>
                    <th class="qb-sort" data-field="netIncome" style="cursor: pointer; text-align: right; white-space: nowrap;">Net Income ↕</th>
                    <th class="qb-sort" data-field="profitMargin" style="cursor: pointer; text-align: right; white-space: nowrap;" title="net income ÷ gross revenue, from the uploaded QuickBooks period">Margin on gross (QB) ↕</th>
                    <th class="qb-sort" data-field="expenseRatio" style="cursor: pointer; text-align: right; white-space: nowrap;">Exp Ratio ↕</th>
                    <th class="qb-sort" data-field="reservations" style="cursor: pointer; text-align: right; white-space: nowrap;">Reservations ↕</th>
                    <th class="qb-sort" data-field="damageWaiverIncome" style="cursor: pointer; text-align: right; white-space: nowrap;">DW Income ↕</th>
                    <th class="qb-sort" data-field="allocatedOverhead" style="cursor: pointer; text-align: right; white-space: nowrap;">Overhead ↕</th>
                    <th class="qb-sort" data-field="effectiveMargin" style="cursor: pointer; text-align: right; white-space: nowrap;">Eff. Margin ↕</th>
                    <th style="white-space: nowrap;">Channels</th>
                  </tr>
                </thead>
                <tbody id="qb-table-body"></tbody>
              </table>
            </div>

            <!-- QB Pagination -->
            <div id="qb-pagination" style="display: none; justify-content: space-between; align-items: center; padding: 12px 16px; border-top: 1px solid var(--border);">
              <div style="font-size: 13px; color: var(--text-muted);">
                Showing <span id="qb-page-start">0</span>–<span id="qb-page-end">0</span> of <span id="qb-page-total">0</span> properties
              </div>
              <div style="display: flex; gap: 8px; align-items: center;">
                <button class="btn btn-sm btn-secondary" id="qb-prev-btn" disabled>← Previous</button>
                <span id="qb-page-label" style="font-size: 13px; color: var(--text-muted);"></span>
                <button class="btn btn-sm btn-secondary" id="qb-next-btn">Next →</button>
              </div>
            </div>

            <!-- QB Totals Strip — flat white, matches .qbt-* card styles in main.css -->
            <div id="qb-totals-strip" style="display: none; padding: 16px 20px; background: var(--bg-subtle); border-top: 1px solid var(--border);">
              <div style="font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); margin-bottom: 12px;">
                Portfolio Total — <span id="qbt-count">0</span> properties (current filter)
              </div>
              <div style="display: flex; flex-wrap: wrap; gap: 0; border: 1px solid var(--border); border-radius: 8px; overflow: hidden; background: #fff;">
                <div class="qbt-cell"><div class="qbt-label">Revenue</div><div class="qbt-val mono num" id="qbt-income">$0</div></div>
                <div class="qbt-cell"><div class="qbt-label">Net Income</div><div class="qbt-val mono num" id="qbt-net">$0</div></div>
                <div class="qbt-cell qbt-cell-highlight"><div class="qbt-label">Margin on gross (QB)</div><div class="qbt-val mono num" id="qbt-margin">0%</div></div>
                <div class="qbt-cell qbt-cell-dim"><div class="qbt-label">Exp Ratio</div><div class="qbt-val mono num" id="qbt-exp-pct">0%</div></div>
                <div class="qbt-cell"><div class="qbt-label">Reservations</div><div class="qbt-val mono num" id="qbt-reservations">0</div></div>
                <div class="qbt-cell"><div class="qbt-label">DW Income</div><div class="qbt-val mono num" id="qbt-dw">$0</div></div>
                <div class="qbt-cell"><div class="qbt-label">Overhead</div><div class="qbt-val mono num" id="qbt-overhead" style="color: var(--warning);">$0</div></div>
                <div class="qbt-cell qbt-cell-dim"><div class="qbt-label">Eff. Margin</div><div class="qbt-val mono num" id="qbt-eff-margin">0%</div></div>
              </div>
            </div>
          </div>
          <div id="qb-error" style="display: none; padding: 16px 20px; color: #dc2626; font-size: 13px;"></div>
          </details>
        </div>

        <!-- 5 · Config strip — monthly projections editor + BLR assumptions -->
        <div class="pnl-config">
          <div class="table-container" style="margin-bottom: 0;">
            <div class="table-header">
              <h3>Monthly projections — ${new Date().getFullYear()}</h3>
              <button id="pnl-proj-save" class="btn btn-primary btn-sm">Save all changes</button>
            </div>
            <div class="pnl-caption">The saved plan behind the Projected columns above and Money&rsquo;s year curve. Actuals come from the live engine and are not editable here.</div>
            <div class="table-scroll">
              <table style="min-width: 420px; width: 100%;">
                <thead>
                  <tr><th>Month</th><th style="text-align:right;">Proj gross</th><th style="text-align:right;">Proj mgmt</th><th style="text-align:right;">Proj net (stored)</th></tr>
                </thead>
                <tbody id="pnl-proj-body"><tr><td colspan="4">${LoadingSpinner.render()}</td></tr></tbody>
                <tfoot id="pnl-proj-foot"></tfoot>
              </table>
            </div>
          </div>
          <div class="table-container" style="margin-bottom: 0;">
            <div class="table-header">
              <h3>BLR projections</h3>
              <button id="pnl-add-blr" class="btn btn-sm btn-primary">+ Add BLR fee</button>
            </div>
            <div class="pnl-caption">Bottom-line revenue assumptions: price per occurrence × take-up rate. Add or delete; to change one, delete and re-add it.</div>
            <div class="table-scroll">
              <table style="min-width: 320px; width: 100%;">
                <thead><tr><th>Fee</th><th style="text-align:right;">Price/occurrence</th><th style="text-align:right;">Take-up</th><th></th></tr></thead>
                <tbody id="pnl-blr-body"><tr><td colspan="4">${LoadingSpinner.render()}</td></tr></tbody>
              </table>
            </div>
          </div>
        </div>

        ${PageFooter.render([
          { abbr: 'GOR',             full: 'Gross Owner Revenue',      note: 'total revenue collected from guests' },
          { abbr: 'MOR / Mgmt rev',  full: 'Management Owner Revenue', note: 'total management income — commission + ancillary fees' },
          { abbr: 'Margin on mgmt rev (modeled)', full: 'Modeled Margin', note: 'modeled net ÷ mgmt rev, where expenses = 72% of mgmt rev × property-type factor — used by the leaderboard and the modeled headline' },
          { abbr: 'Margin on gross (QB)', full: 'QuickBooks Margin',   note: 'QuickBooks net income ÷ QB gross revenue for the uploaded period — individual property columns only; excludes COO and Executive classes' },
          { abbr: 'Exp Ratio',       full: 'Expense Ratio',            note: 'Total Expenses ÷ Revenue — share of revenue consumed by operating costs (QB: on gross; modeled: on mgmt rev)' },
          { abbr: 'DW Income',       full: 'Damage Waiver Income',     note: 'revenue collected from damage waiver / damage protection fees' },
          { abbr: 'Overhead',        full: 'Allocated Overhead',       note: 'shared 0-Overhead class costs distributed across properties in proportion to reservation count' },
          { abbr: 'Eff. Margin',     full: 'Effective Margin',         note: '(QB net income − allocated overhead) ÷ QB gross revenue — bottom-line margin after overhead allocation' },
          { abbr: 'Proj Gross / Proj Mgmt', full: 'Monthly projection', note: 'the saved monthly plan (AirROI / PriceLabs generated or hand-edited in the config strip); feeds the Money year curve' },
          { abbr: 'BLR',             full: 'Bottom Line Revenue',      note: 'ancillary fee assumptions — price per occurrence × expected take-up rate' },
        ])}
      </div>
    `;

    this.loadQBFromStorage();
    await this.loadData();
    this.bindEvents();
    // Independent sections below the headline — fire in parallel, render as they land.
    void this.loadProjSummary();
    void this.loadRevenueMetrics();
    void this.loadBLR();
  }

  // ── QB Storage ─────────────────────────────────────────────────────────────

  private normalizeName(name: string): string {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Canonicalize street type abbreviations so "Boulevard"↔"Blvd", "Drive"↔"Dr", etc. all match.
  private canonicalizeToken(t: string): string {
    const MAP: Record<string, string> = {
      avenue: 'ave', boulevard: 'blvd', street: 'st', court: 'ct',
      place: 'pl', drive: 'dr', road: 'rd', lane: 'ln',
      circle: 'cir', highway: 'hwy', terrace: 'ter', trail: 'trl',
      parkway: 'pkwy', walk: 'walk', way: 'way',
      north: 'n', south: 's', east: 'e', west: 'w',
    };
    return MAP[t] ?? t;
  }

  // Tokenize a RAW (un-normalized) string — preserves word boundaries before normalization
  // strips punctuation/spaces first so "3608 Oleander Dr" → ["3608","oleander","dr"] not ["3608","oleanderdr"]
  private tokenizeRaw(s: string): string[] {
    return s.toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(t => t.length > 0)
      .map(t => this.canonicalizeToken(t));
  }

  // Token-based match: ALL numeric tokens AND all ≥3-char word tokens from QB must appear in target.
  // ("Melba 9" won't match "Melba Cabin 1"; use tokenScore to disambiguate "Unit A" vs "Unit B".)
  private tokenMatch(qbRaw: string, targetRaw: string): boolean {
    const qt = this.tokenizeRaw(qbRaw);
    const tt = new Set(this.tokenizeRaw(targetRaw));
    const nums  = qt.filter(t => /^\d+$/.test(t));
    const words = qt.filter(t => /^[a-z]{3,}$/.test(t));
    if (nums.length === 0 && words.length === 0) return false;
    if (nums.length  > 0 && !nums.every(n  => tt.has(n))) return false;
    if (words.length > 0 && !words.every(w => tt.has(w))) return false;
    return true;
  }

  // Count how many QB tokens appear in target — used to pick the best among multiple tokenMatch candidates.
  private tokenScore(qbRaw: string, targetRaw: string): number {
    const qt = this.tokenizeRaw(qbRaw);
    const tt = new Set(this.tokenizeRaw(targetRaw));
    return qt.filter(t => tt.has(t)).length;
  }

  private loadQBListingMap(): void {
    try {
      const raw = localStorage.getItem(QB_MAP_KEY);
      if (raw) this.qbListingMap = JSON.parse(raw);
    } catch (_) {}
  }

  private saveQBListingMap(): void {
    localStorage.setItem(QB_MAP_KEY, JSON.stringify(this.qbListingMap));
  }

  private loadQBFromStorage(): void {
    try {
      const raw = localStorage.getItem(QB_STORAGE_KEY);
      if (raw) this.qbPeriods = JSON.parse(raw);
    } catch (_) {}

    this.loadQBListingMap();

    if (this.qbPeriods.length > 0) {
      this.qbActivePeriodIdx = this.qbPeriods.length - 1;
      this.renderPeriodChips();
      this.refreshQBView();
      this.fetchPeriodReservationCounts();
    }
  }

  private saveQBToStorage(): void {
    localStorage.setItem(QB_STORAGE_KEY, JSON.stringify(this.qbPeriods));
  }

  // ── Reservation counts for overhead allocation ─────────────────────────────

  private async fetchPeriodReservationCounts(): Promise<void> {
    const period = this.getActivePeriod();
    if (!period) return;

    this.qbResCountsLoaded = false;
    try {
      const params = new URLSearchParams();
      if (period.periodStart) params.set('start', period.periodStart);
      if (period.periodEnd)   params.set('end',   period.periodEnd);

      // Fetch reservation counts, Hostaway listings, and Supabase listings in parallel
      if (!this.allListings.length) {
        try {
          const r = await fetch('/api/listings');
          if (r.ok) {
            this.allListings = await r.json();
            this.listingById = new Map(this.allListings.map(l => [String(l.id), l]));
          }
        } catch (_) {}
      }

      const [countsResp, haResp] = await Promise.all([
        fetch(`/api/reservations/counts?${params}`),
        fetch('/api/hostaway/listings'),
      ]);
      if (!countsResp.ok) throw new Error(`counts HTTP ${countsResp.status}`);

      const rows: { listing_id: number; name: string; short_name?: string; pms_listing_id: string; count: number }[] = await countsResp.json();

      // Build pms_listing_id → count and pms_listing_id → UUID
      const pmsToCounts: Record<string, number> = {};
      const pmsToUuid: Record<string, string> = {};
      this.listingIdToCount = {};
      for (const row of rows) {
        if (row.pms_listing_id) {
          pmsToCounts[row.pms_listing_id] = row.count;
          pmsToUuid[row.pms_listing_id]   = String(row.listing_id);
        }
        this.listingIdToCount[row.listing_id] = row.count;
      }

      // Seed qbNormToListingId from manual map (highest priority, always wins)
      this.qbNormToListingId = {};
      for (const [norm, id] of Object.entries(this.qbListingMap)) {
        this.qbNormToListingId[norm] = String(id);
      }

      type HAListing = { hostaway_id: string; name: string | null; internal_name: string | null; address: string | null; city: string | null; nickname: string | null };
      const haListings: HAListing[] = haResp.ok ? await haResp.json() : [];

      // HATarget stores both compact-norm (for exact/contains) and raw text (for tokenMatch)
      type HATarget = { hostaway_id: string; norm: string; raw: string; label: string };
      const haTargets: HATarget[] = [];
      for (const l of haListings) {
        const add = (s: string | null, lbl?: string) => {
          if (!s) return;
          const n = this.normalizeName(s);
          if (n) haTargets.push({ hostaway_id: l.hostaway_id, norm: n, raw: s, label: lbl ?? s });
        };
        add(l.internal_name);
        // Strip area-code prefix from internalListingName: "NP - 3571 Quince" → "3571 Quince"
        if (l.internal_name) {
          const stripped = l.internal_name.replace(/^[a-zA-Z\s_-]{1,10}[-_]\s*/, '').trim();
          if (stripped && stripped !== l.internal_name) add(stripped, `${l.internal_name} [no-prefix]`);
        }
        add(l.nickname);
        // Full address
        if (l.address) {
          add(l.address);
          // Street-only: just "3571 Quince St" (before first comma)
          const streetOnly = l.address.split(',')[0].trim();
          if (streetOnly !== l.address) add(streetOnly, `${l.address} [street]`);
        }
        // Composite: street portion + stripped internal name
        // Handles QB names like "Bayside Unit A, 3525" where number is in address but "Unit A" is in internal name
        if (l.address && l.internal_name) {
          const streetPart    = l.address.split(',')[0].trim();
          const strippedInt   = l.internal_name.replace(/^[a-zA-Z\s_-]{1,10}[-_]\s*/, '').trim();
          if (strippedInt && strippedInt !== l.internal_name) {
            add(`${streetPart} ${strippedInt}`, `${l.internal_name} + ${streetPart} [composite]`);
            // Also add without the leading street number so "Avenida Aragon - Unit B" (no number) can match
            const streetNameOnly = streetPart.replace(/^\d+\s*/, '').trim();
            if (streetNameOnly && streetNameOnly !== streetPart) {
              add(`${streetNameOnly} ${strippedInt}`, `${l.internal_name} + ${streetNameOnly} [composite-nonum]`);
            }
          }
        }
        add(l.name);
        // pipe-separated segments (Airbnb-style slugs: "Title|Subtitle|Address")
        if (l.name) l.name.split('|').forEach(seg => add(seg.trim(), `${l.name} [${seg.trim()}]`));
      }

      console.log(`[QB] Hostaway targets built: ${haListings.length} listings → ${haTargets.length} search candidates`);

      const counts: Record<string, number> = {};

      for (const qbProp of period.rawProps) {
        const qbNorm = this.normalizeName(qbProp.name);
        if (!qbNorm) continue;

        // 1. Saved manual mapping (highest priority — UUID already seeded above)
        const mappedId = this.qbListingMap[qbNorm];
        if (mappedId !== undefined) {
          counts[qbNorm] = this.listingIdToCount[mappedId] ?? 0;
          continue;
        }

        // 2. Match against Hostaway listing names → resolve via pms_listing_id
        //    For tokenMatch candidates, pick the highest-scoring one (most overlapping tokens)
        //    so "Unit B" beats "Unit A" for QB name "Avenida Aragon - Unit B".
        const tokenCandidates = haTargets.filter(t => this.tokenMatch(qbProp.name, t.raw));
        const bestToken = tokenCandidates.length > 0
          ? tokenCandidates.reduce((best, t) =>
              this.tokenScore(qbProp.name, t.raw) > this.tokenScore(qbProp.name, best.raw) ? t : best)
          : undefined;
        const haHit = haTargets.find(t => t.norm === qbNorm)
          || haTargets.find(t => t.norm.includes(qbNorm) || (qbNorm.includes(t.norm) && t.norm.length > 4))
          || bestToken;
        const haCount: number | null = haHit ? (pmsToCounts[haHit.hostaway_id] ?? 0) : null;

        // 3. Supabase name/short_name fallback — always runs, fills gap when pms_listing_id is missing
        let sbCount: number | null = null;
        for (const row of rows) {
          const candidates = [row.name, row.short_name ?? ''].filter(Boolean);
          const hit = candidates.some(c => {
            const cn = this.normalizeName(c);
            return cn === qbNorm
              || cn.includes(qbNorm)
              || (qbNorm.includes(cn) && cn.length > 4)
              || this.tokenMatch(qbProp.name, c);
          });
          if (hit) { sbCount = row.count; break; }
        }

        // Prefer whichever source returns a non-zero count; fall back to 0 if matched but no reservations
        if (haCount !== null || sbCount !== null) {
          const resolved = (haCount !== null && haCount > 0) ? haCount
                         : (sbCount !== null && sbCount > 0) ? sbCount
                         : 0;
          counts[qbNorm] = resolved;

          // Capture the resolved listing UUID for channel links
          if (haHit && pmsToUuid[haHit.hostaway_id]) {
            this.qbNormToListingId[qbNorm] = pmsToUuid[haHit.hostaway_id];
            console.log(`[QB] "${qbProp.name}" → HA "${haHit.label}" (ha=${haCount ?? 'n/a'} sb=${sbCount ?? 'n/a'}) → ${resolved}`);
          } else {
            // Supabase name match — find the row we hit
            for (const row of rows) {
              const candidates = [row.name, row.short_name ?? ''].filter(Boolean);
              const hit = candidates.some(c => {
                const cn = this.normalizeName(c);
                return cn === qbNorm || cn.includes(qbNorm) || (qbNorm.includes(cn) && cn.length > 4) || this.tokenMatch(qbProp.name, c);
              });
              if (hit) { this.qbNormToListingId[qbNorm] = String(row.listing_id); break; }
            }
            console.log(`[QB] "${qbProp.name}" → Supabase name match → ${resolved}`);
          }
        }
      }

      const matchedCount = Object.keys(counts).length;
      this.qbResCounts = counts;
      this.qbResCountsLoaded = true;

      console.log(`[QB] ${matchedCount}/${period.rawProps.length} props matched for ${period.periodStart ?? '?'} – ${period.periodEnd ?? '?'}`);
      // Distinguish: no Hostaway listing found vs matched but 0 reservations this period
      const trulyUnmatched = period.rawProps.filter(p => counts[this.normalizeName(p.name)] === undefined).map(p => p.name);
      const zeroRes        = period.rawProps.filter(p => counts[this.normalizeName(p.name)] === 0).map(p => p.name);
      if (trulyUnmatched.length) console.warn('[QB] NO HOSTAWAY MATCH (' + trulyUnmatched.length + '):\n' + trulyUnmatched.map((n,i) => `  ${i+1}. "${n}"`).join('\n'));
      if (zeroRes.length)        console.info('[QB] Matched but 0 reservations in period (' + zeroRes.length + '):\n' + zeroRes.map((n,i) => `  ${i+1}. "${n}"`).join('\n'));

      this.refreshQBView(false);
    } catch (e) {
      console.error('[QB] fetchPeriodReservationCounts exception:', e);
      this.refreshQBView(false);
    }
  }

  // ── Reservation nights ─────────────────────────────────────────────────────


  // ── CSV parsing ────────────────────────────────────────────────────────────

  private parseCSVLine(line: string): string[] {
    const fields: string[] = [];
    let cur = '';
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
        else { inQuote = !inQuote; }
      } else if (ch === ',' && !inQuote) {
        fields.push(cur.trim()); cur = '';
      } else {
        cur += ch;
      }
    }
    fields.push(cur.trim());
    return fields;
  }

  private parseVal(s: string): number {
    if (!s) return 0;
    const isNeg = /^\(.*\)$/.test(s.trim());
    const cleaned = s.replace(/[$,\s()]/g, '');
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : (isNeg ? -Math.abs(n) : n);
  }

  private parseQBDateRange(lines: string[]): { periodStart: string | null; periodEnd: string | null } {
    const MONTHS: Record<string, number> = {
      january:1, february:2, march:3, april:4, may:5, june:6,
      july:7, august:8, september:9, october:10, november:11, december:12,
    };
    for (let i = 0; i < Math.min(12, lines.length); i++) {
      const raw = this.parseCSVLine(lines[i])[0]?.trim().replace(/^"|"$/g, '') || '';

      // "January 2025"
      const mY = raw.match(/^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})$/i);
      if (mY) {
        const m = MONTHS[mY[1].toLowerCase()];
        const y = parseInt(mY[2]);
        const last = new Date(y, m, 0).getDate();
        return {
          periodStart: `${y}-${String(m).padStart(2,'0')}-01`,
          periodEnd:   `${y}-${String(m).padStart(2,'0')}-${String(last).padStart(2,'0')}`,
        };
      }

      // "MM/DD/YYYY - MM/DD/YYYY" or "MM/DD/YYYY – MM/DD/YYYY"
      const dR = raw.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*[-–]\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (dR) {
        return {
          periodStart: `${dR[3]}-${dR[1].padStart(2,'0')}-${dR[2].padStart(2,'0')}`,
          periodEnd:   `${dR[6]}-${dR[4].padStart(2,'0')}-${dR[5].padStart(2,'0')}`,
        };
      }
    }
    return { periodStart: null, periodEnd: null };
  }

  private parseQBCSV(text: string): Omit<QBPeriod, 'id' | 'label' | 'uploadedAt'> {
    const lines = text.split(/\r?\n/);

    // Parse date range from header rows before the property row
    const { periodStart, periodEnd } = this.parseQBDateRange(lines);

    // Find header row (contains a class aggregate column like "1 - Managed")
    let headerIdx = -1;
    let headers: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const row = this.parseCSVLine(lines[i]);
      if (row.some(c => /^\d+\s*-\s*\w/i.test(c.trim()))) {
        headerIdx = i; headers = row; break;
      }
    }
    if (headerIdx === -1) throw new Error('Could not find the property header row. Make sure this is a QuickBooks "Profit and Loss by Class" export.');

    // Detect class boundaries
    const classBoundaries: { idx: number; className: string }[] = [];
    let overheadAggColIdx = -1;

    for (let i = 1; i < headers.length; i++) {
      const cell = headers[i].trim();
      const m = cell.match(/^(\d+)\s*-\s*(.+)$/i);
      if (m && !cell.toLowerCase().startsWith('total for')) {
        classBoundaries.push({ idx: i, className: m[2].trim() });
        if (/^0\s*-\s*overhead/i.test(cell)) overheadAggColIdx = i;
      }
    }

    // Identify individual property columns; separate COO/Executive/NotSpecified/Overhead
    const propCols: { idx: number; name: string; classGroup: string }[] = [];
    const notSpecCols: { idx: number; name: string }[] = [];

    for (let i = 1; i < headers.length; i++) {
      const name = headers[i].trim();
      if (!name || isAggCol(name)) continue;

      let classGroup = 'Unknown';
      for (const b of classBoundaries) {
        if (b.idx > i) { classGroup = b.className; break; }
      }

      if (EXCLUDED_CLASSES.has(classGroup.toLowerCase())) continue;
      if (/not specified/i.test(classGroup)) { notSpecCols.push({ idx: i, name }); continue; }
      if (/^overhead$/i.test(classGroup)) continue;

      propCols.push({ idx: i, name, classGroup });
    }

    if (propCols.length === 0) throw new Error('No individual property columns found after excluding COO/Executive. Check the CSV format.');

    // Accumulators
    type Acc = QBPropertyRaw;
    const acc: Record<string, Acc> = {};
    propCols.forEach(({ name, classGroup }) => {
      acc[name] = { name, classGroup, income: 0, cogs: 0, grossProfit: 0, expenses: 0, netIncome: 0,
        cleaningIncome: 0, cleaningCosts: 0, damageWaiverIncome: 0 };
    });
    const nsAcc: Record<string, Acc> = {};
    notSpecCols.forEach(({ name }) => {
      nsAcc[name] = { name, classGroup: 'Not Specified', income: 0, cogs: 0, grossProfit: 0, expenses: 0, netIncome: 0,
        cleaningIncome: 0, cleaningCosts: 0, damageWaiverIncome: 0 };
    });

    // Overhead line items from the aggregate column
    const overheadExpItems: Record<string, number> = {};
    let overheadAggTotalExpenses = 0;

    // Section tracking
    let inIncome = false;
    let inCogs = false;
    let inExpenses = false;

    const TOTAL_ROWS: Record<string, keyof Acc> = {
      'total income': 'income', 'total revenue': 'income', 'total for income': 'income',
      'total cost of goods sold': 'cogs', 'total for cost of goods sold': 'cogs',
      'gross profit': 'grossProfit',
      'total expenses': 'expenses', 'total for expenses': 'expenses', 'total operating expenses': 'expenses',
      'net operating income': 'netIncome', 'net income': 'netIncome',
    };

    for (let i = headerIdx + 1; i < lines.length; i++) {
      const row = this.parseCSVLine(lines[i]);
      if (!row.length || !row[0]) continue;
      const rawLabel = row[0].trim();
      const label = rawLabel.toLowerCase();

      // Section headers
      if (label === 'income' || label === 'revenue') { inIncome = true; inCogs = false; inExpenses = false; continue; }
      if (label === 'cost of goods sold' || label === 'cogs') { inIncome = false; inCogs = true; inExpenses = false; continue; }
      if (label === 'expenses' || label === 'operating expenses') { inIncome = false; inCogs = false; inExpenses = true; continue; }

      const totalKey = TOTAL_ROWS[label] as keyof Acc | undefined;

      if (totalKey) {
        propCols.forEach(({ idx, name }) => {
          const val = this.parseVal(row[idx] || '');
          (acc[name] as any)[totalKey] = totalKey === 'cogs' ? Math.abs(val) : val;
        });
        notSpecCols.forEach(({ idx, name }) => {
          const val = this.parseVal(row[idx] || '');
          (nsAcc[name] as any)[totalKey] = totalKey === 'cogs' ? Math.abs(val) : val;
        });
        if (totalKey === 'expenses' && overheadAggColIdx >= 0) {
          overheadAggTotalExpenses = Math.abs(this.parseVal(row[overheadAggColIdx] || ''));
        }
        if (label === 'gross profit') { inIncome = false; inCogs = false; }
        continue;
      }

      // Income sub-rows: cleaning income, damage waiver
      if (inIncome) {
        if (/cleaning.*income|cleaning.*fee.*income/i.test(rawLabel)) {
          propCols.forEach(({ idx, name }) => { acc[name].cleaningIncome += this.parseVal(row[idx] || ''); });
        }
        if (/damage.*waiver|damage.*protect/i.test(rawLabel)) {
          propCols.forEach(({ idx, name }) => { acc[name].damageWaiverIncome += this.parseVal(row[idx] || ''); });
        }
      }

      // COGS sub-rows: cleaning costs
      if (inCogs) {
        if (/cleaning.*cost|cleaning.*expense|cleaning.*supply/i.test(rawLabel)) {
          propCols.forEach(({ idx, name }) => { acc[name].cleaningCosts += Math.abs(this.parseVal(row[idx] || '')); });
        }
      }

      // Expense sub-rows: capture overhead aggregate column values per line item
      if (inExpenses && overheadAggColIdx >= 0 && !label.startsWith('total')) {
        const aggVal = Math.abs(this.parseVal(row[overheadAggColIdx] || ''));
        if (aggVal > 0) overheadExpItems[rawLabel] = (overheadExpItems[rawLabel] || 0) + aggVal;
      }
    }

    // Build overhead items list
    let overheadItems: QBOverheadItem[] = Object.entries(overheadExpItems)
      .filter(([, amt]) => amt > 0)
      .map(([rowLabel, amount]) => ({ rowLabel, amount }));

    if (overheadItems.length === 0 && overheadAggTotalExpenses > 0) {
      overheadItems = [{ rowLabel: 'Total Overhead', amount: overheadAggTotalExpenses }];
    }

    const rawProps = Object.values(acc).filter(p => p.income !== 0 || p.netIncome !== 0 || p.expenses !== 0);
    const notSpecifiedProps = Object.values(nsAcc).filter(p => p.income !== 0 || p.netIncome !== 0 || p.expenses !== 0);

    return { periodStart, periodEnd, rawProps, overheadItems, notSpecifiedProps };
  }

  // ── Overhead allocation ────────────────────────────────────────────────────

  private applyOverheadAllocation(rawProps: QBPropertyRaw[], overheadItems: QBOverheadItem[]): QBPropertyComputed[] {
    if (rawProps.length === 0) return [];

    const totalOverhead = overheadItems.reduce((sum, item) => sum + item.amount, 0);
    console.log(`[QB] applyOverhead: ${rawProps.length} props, ${overheadItems.length} overhead items, totalOverhead=${totalOverhead}, resLoaded=${this.qbResCountsLoaded}, resCounts=${JSON.stringify(this.qbResCounts).slice(0,200)}`);
    const alloc: Record<string, number> = {};
    rawProps.forEach(p => { alloc[p.name] = 0; });

    if (totalOverhead > 0 && this.qbResCountsLoaded) {
      // Allocate overhead only to properties with reservations that month; others get 0
      const totalRes = rawProps.reduce((sum, p) => sum + (this.qbResCounts[this.normalizeName(p.name)] || 0), 0);
      if (totalRes > 0) {
        rawProps.forEach(p => {
          const res = this.qbResCounts[this.normalizeName(p.name)] || 0;
          alloc[p.name] = Math.round(totalOverhead * (res / totalRes) * 100) / 100;
        });
      }
      // if totalRes = 0 (no matches yet), alloc stays all zeros — use Map Properties to fix
    }

    return rawProps.map(p => {
      const allocatedOverhead = Math.round(alloc[p.name] * 100) / 100;
      const norm              = this.normalizeName(p.name);
      const rawCount          = this.qbResCounts[norm];
      const isMapped          = rawCount !== undefined;
      const reservations      = rawCount ?? 0;
      const profitMargin    = p.income !== 0 ? Math.round((p.netIncome / p.income) * 1000) / 10 : 0;
      const expenseRatio    = p.income !== 0 ? Math.round((p.expenses  / p.income) * 1000) / 10 : 0;
      const effectiveMargin = p.income !== 0
        ? Math.round(((p.netIncome - allocatedOverhead) / p.income) * 1000) / 10 : 0;
      return { ...p, reservations, isMapped, profitMargin, expenseRatio, allocatedOverhead, effectiveMargin };
    });
  }

  // ── Filter / sort ──────────────────────────────────────────────────────────

  private getActivePeriod(): QBPeriod | null {
    return this.qbPeriods[this.qbActivePeriodIdx] ?? null;
  }

  private getComputedData(): QBPropertyComputed[] {
    const period = this.getActivePeriod();
    if (!period) return [];
    return this.applyOverheadAllocation(period.rawProps, period.overheadItems);
  }

  private getFilteredData(): QBPropertyComputed[] {
    const search = this.qbSearch.toLowerCase();
    return this.getComputedData()
      .filter(p => {
        if (search && !p.name.toLowerCase().includes(search)) return false;
        if (this.qbFilterClass && p.classGroup !== this.qbFilterClass) return false;
        if (this.qbFilterProfit === 'profitable' && p.netIncome <= 0) return false;
        if (this.qbFilterProfit === 'loss' && p.netIncome >= 0) return false;
        if (this.qbMinRevenue > 0 && p.income < this.qbMinRevenue) return false;
        return true;
      })
      .sort((a, b) => {
        const mult = this.qbSortDir === 'desc' ? -1 : 1;
        const av = a[this.qbSortField] as any;
        const bv = b[this.qbSortField] as any;
        if (typeof av === 'string') return av.localeCompare(bv) * mult;
        return (av - bv) * mult;
      });
  }

  private refreshQBView(resetPage = true): void {
    if (resetPage) this.qbPage = 1;
    const period = this.getActivePeriod();
    const hasData = !!period && period.rawProps.length > 0;

    const show = (id: string, vis: boolean) => {
      const el = document.getElementById(id);
      if (el) el.style.display = vis ? '' : 'none';
    };

    show('qb-filters', hasData);
    show('qb-clear-btn',  this.qbPeriods.length > 0);
    show('qb-map-btn',    hasData);
    show('qb-export-btn', hasData);

    // Headline band + monthly statement react to every period change
    // (upload, switch, delete) — they are the page's QB-vs-modeled face.
    this.renderHeadline();
    this.renderMonthlyStatement();

    if (!hasData) {
      show('qb-chart-container', false);
      show('qb-table-container', false);
      show('qb-not-specified', false);
      show('qb-overhead-panel', false);
      show('qb-trend-container', false);
      return;
    }

    const filtered = this.getFilteredData();
    const computed = this.getComputedData();

    this.renderTrendChart();
    this.renderNotSpecified(period!.notSpecifiedProps);
    this.renderOverheadPanel(period!.overheadItems, period!.rawProps);
    this.populateClassDropdown(computed);
    this.updateQBCounts(filtered, computed);
    this.renderQBChart(filtered);
    this.renderQBTable(filtered);
  }

  // ── QB renderers ───────────────────────────────────────────────────────────

  private renderPeriodChips(): void {
    const chips = document.getElementById('qb-period-chips');
    if (!chips) return;

    if (this.qbPeriods.length === 0) {
      chips.innerHTML = '<span style="font-size: 12px; color: var(--text-muted);">No periods loaded — upload a CSV to get started</span>';
      return;
    }

    chips.innerHTML = this.qbPeriods.map((p, i) => {
      const isActive = i === this.qbActivePeriodIdx;
      return `
        <span class="qb-period-chip" data-idx="${i}" style="
          display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px;
          border-radius: 20px; font-size: 12px; font-weight: 600; cursor: pointer;
          background: ${isActive ? 'var(--accent)' : 'var(--bg-subtle)'};
          color: ${isActive ? '#fff' : 'var(--text-muted)'};
          border: 1px solid ${isActive ? 'var(--accent)' : 'var(--border)'};
        ">
          ${Formatters.escapeHtml(p.label)}
          <button data-delete-idx="${i}" style="background: none; border: none; cursor: pointer; padding: 0; line-height: 1; color: ${isActive ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)'}; font-size: 14px;" title="Remove period">×</button>
        </span>
      `;
    }).join('');

    chips.querySelectorAll('.qb-period-chip').forEach(chip => {
      chip.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const deleteIdx = target.dataset.deleteIdx;
        if (deleteIdx !== undefined) {
          this.qbPeriods.splice(parseInt(deleteIdx), 1);
          if (this.qbActivePeriodIdx >= this.qbPeriods.length) {
            this.qbActivePeriodIdx = Math.max(0, this.qbPeriods.length - 1);
          }
          this.saveQBToStorage();
          this.renderPeriodChips();
          this.refreshQBView();
          return;
        }
        const idx = (chip as HTMLElement).dataset.idx;
        if (idx !== undefined) {
          this.qbActivePeriodIdx = parseInt(idx);
          this.qbResCounts = {};
          this.qbResCountsLoaded = false;
          this.renderPeriodChips();
          this.refreshQBView();
          this.fetchPeriodReservationCounts();
        }
      });
    });
  }

  // ── 1 · P&L headline band ───────────────────────────────────────────────────
  // QuickBooks actuals when a period is loaded; the expense model otherwise —
  // the eyebrow always names the source so the model is never read as actuals.
  private renderHeadline(): void {
    const band = document.getElementById('pnl-band');
    const src  = document.getElementById('pnl-band-source');
    if (!band) return;

    const cellIcon = (label: string): string => {
      const l = label.toLowerCase();
      if (l.includes('revenue')) return icon('wallet', 17);
      if (l.includes('net'))     return icon('trending', 17);
      if (l.includes('margin'))  return icon('target', 17);
      if (l.includes('expense')) return icon('receipt', 17);
      if (l.includes('overhead')) return icon('package', 17);
      return icon('wallet', 17);
    };
    const cell = (label: string, value: string, sub: string, title = '') => `
      <div class="kpi-card"${title ? ` title="${Formatters.escapeHtml(title)}"` : ''}>
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${cellIcon(label)}</span></div>
        <div class="kpi-card-value">${value}</div>
        <div class="kpi-card-cap">${sub}</div>
      </div>`;

    const period = this.getActivePeriod();
    if (period && period.rawProps.length) {
      const computed  = this.getComputedData();
      const totIncome = computed.reduce((s, p) => s + p.income, 0);
      const totNet    = computed.reduce((s, p) => s + p.netIncome, 0);
      const totExp    = computed.reduce((s, p) => s + p.expenses, 0);
      const overheadPool = period.overheadItems.reduce((s, i) => s + i.amount, 0);
      const margin   = totIncome ? Math.round((totNet / totIncome) * 1000) / 10 : null;
      const expRatio = totIncome ? Math.round((totExp / totIncome) * 1000) / 10 : null;
      const range = period.periodStart && period.periodEnd
        ? `${period.periodStart} → ${period.periodEnd}` : 'period dates not found in CSV';
      const uploaded = new Date(period.uploadedAt).toLocaleDateString();
      if (src) src.textContent = `QuickBooks · ${period.label} · ${range} · uploaded ${uploaded}`;
      this.renderDenominator(null); // QB path has its own class-level disclosures
      band.innerHTML =
        cell('Revenue', ProfitabilityPage.money(totIncome),
          `QB income · ${computed.length} property cols (COO/Exec excluded)`) +
        cell('Net income', ProfitabilityPage.money(totNet),
          'property columns · before overhead pool') +
        cell('Margin', ProfitabilityPage.pct(margin),
          'margin on gross (QB) · net ÷ revenue') +
        cell('Expense ratio', ProfitabilityPage.pct(expRatio),
          'QB expenses ÷ revenue') +
        cell('Overhead pool', ProfitabilityPage.money(overheadPool),
          '0-Overhead class · allocated by reservations in the workbench');
      return;
    }

    // No QB period — modeled fallback, clearly labelled as a model.
    if (src) src.textContent = 'Modeled · expenses = 72% of mgmt rev × type factor — no QuickBooks period loaded';
    const p = this.data?.portfolio;
    if (!p) {
      band.innerHTML = `<div style="grid-column:1/-1;">${LoadingSpinner.render()}</div>`;
      return;
    }
    const modExpRatio = p.mor ? Math.round((p.estimated_expenses / p.mor) * 1000) / 10 : null;
    band.innerHTML =
      cell('Revenue (mgmt)', ProfitabilityPage.money(p.mor),
        `mgmt rev · all synced reservations · gross ${ProfitabilityPage.money(p.gor)}`) +
      cell('Net income', ProfitabilityPage.money(p.net_profit),
        'modeled · mgmt rev − modeled expenses') +
      cell('Margin', ProfitabilityPage.pct(p.margin_percent),
        'margin on mgmt rev (modeled)') +
      cell('Expense ratio', ProfitabilityPage.pct(modExpRatio),
        'modeled expenses ÷ mgmt rev') +
      cell('Overhead pool', '—',
        'needs a QuickBooks upload (0-Overhead class)',
        'Upload a QuickBooks "Profit and Loss by Class" CSV in the workbench below to see the real overhead pool.');
    this.renderDenominator(p);
  }

  // Loom #4: make the modeled-margin denominator auditable — show "N of M
  // properties, K excluded" with each exclusion reason and the property list.
  private renderDenominator(p: any): void {
    const el = document.getElementById('pnl-denominator');
    if (!el) return;
    if (!p || p.total_count == null) { el.innerHTML = ''; return; }
    const excluded: any[] = (this.data && this.data.excluded) || [];
    const REASON: Record<string, string> = {
      manually_excluded: 'Manually excluded', inactive_status: 'Inactive (status)',
      inactive_type: 'Inactive (type)', zero_revenue: 'No revenue in window',
    };
    const order = ['manually_excluded', 'inactive_status', 'inactive_type', 'zero_revenue'];
    const groups: Record<string, any[]> = {};
    excluded.forEach(x => { (groups[x.reason] = groups[x.reason] || []).push(x); });
    const chips = order.filter(r => groups[r]).map(r => `<span class="pnl-exr">${REASON[r] || r} · ${groups[r].length}</span>`).join('');
    const lists = order.filter(r => groups[r]).map(r => `
      <div class="pnl-exg"><div class="pnl-exg-h">${REASON[r] || r} (${groups[r].length})</div>
        <div class="pnl-exg-l">${groups[r].map(x => Formatters.escapeHtml(x.name)).join(' · ')}</div></div>`).join('');
    el.innerHTML = `
      <style>
        .pnl-denom { font-size:12px; color:var(--text-muted); margin:0 0 20px; }
        .pnl-denom .pnl-count { font-weight:700; color:var(--text); }
        .pnl-exr { display:inline-block; font-size:10.5px; background:var(--bg-subtle); border:1px solid var(--border); border-radius:999px; padding:2px 9px; margin-left:6px; color:var(--text-muted); }
        .pnl-denom details { margin-top:8px; }
        .pnl-denom summary { cursor:pointer; font-size:11.5px; color:var(--accent); font-weight:600; }
        .pnl-exg { margin:9px 0; }
        .pnl-exg-h { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); margin-bottom:2px; }
        .pnl-exg-l { font-size:11.5px; color:var(--text-muted); line-height:1.6; }
      </style>
      <div class="pnl-denom">
        Margin is computed over <span class="pnl-count">${p.included_count} of ${p.total_count}</span> properties${p.excluded_count ? ` · ${p.excluded_count} excluded` : ''}.${chips}
        ${excluded.length ? `<details><summary>Show excluded properties</summary>${lists}</details>` : ''}
      </div>`;
  }

  // ── 2 · Monthly P&L statement ───────────────────────────────────────────────

  // The latest-uploaded QB period whose start AND end fall inside the given
  // month (i.e. a monthly export). Quarterly/annual uploads are ignored here —
  // repeating a multi-month total on each covered month would multi-count it.
  private monthCoveredBy(year: number, month: number): QBPeriod | null {
    const mm = String(month).padStart(2, '0');
    const first = `${year}-${mm}-01`;
    const last  = `${year}-${mm}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
    for (let i = this.qbPeriods.length - 1; i >= 0; i--) {
      const p = this.qbPeriods[i];
      if (p.periodStart && p.periodEnd && p.periodStart >= first && p.periodEnd <= last) return p;
    }
    return null;
  }

  private renderMonthlyStatement(): void {
    const wrap = document.getElementById('pnl-monthly-wrap');
    if (!wrap) return;
    if (!this.projLoaded) { wrap.innerHTML = LoadingSpinner.render(); return; }
    if (!this.projRows.length) {
      wrap.innerHTML = '<div class="empty-state" style="padding:24px;">Could not load the live monthly summary (/api/projections/summary). Reload to retry.</div>';
      return;
    }

    const year = this.projYear;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    const currentMonth = now.getFullYear() === year ? now.getMonth() + 1 : (now.getFullYear() > year ? 13 : 0);

    type QBCell = { period: QBPeriod; income: number; net: number };
    const qbByMonth = new Map<number, QBCell>();
    for (let m = 1; m <= 12; m++) {
      const p = this.monthCoveredBy(year, m);
      if (!p) continue;
      qbByMonth.set(m, {
        period: p,
        income: p.rawProps.reduce((s, r) => s + r.income, 0),
        net:    p.rawProps.reduce((s, r) => s + r.netIncome, 0),
      });
    }
    const coveredMonths = [...qbByMonth.keys()];
    const latestUpload = coveredMonths.length
      ? [...qbByMonth.values()].map(c => c.period.uploadedAt).sort().slice(-1)[0]
      : null;
    const qbHeader = latestUpload
      ? `QuickBooks actuals (uploaded ${new Date(latestUpload).toLocaleDateString()})`
      : 'QuickBooks actuals (no uploads cover this year)';

    const money = (n: number) => `<span class="mono num">${ProfitabilityPage.money(n)}</span>`;
    const noQB = `<td class="num" style="color:var(--text-muted);" title="no QB upload for this month">—</td>`;

    const tot = { gross: 0, mgmt: 0, projGross: 0, projMgmt: 0, qbIncome: 0, qbNet: 0 };
    const rows = this.projRows.map(r => {
      tot.gross += r.actual_gross; tot.mgmt += r.actual_mgmt_fee;
      tot.projGross += r.projected_gross; tot.projMgmt += r.projected_mgmt_fee;
      const qb = qbByMonth.get(r.month);
      let qbCells = noQB + noQB;
      if (qb) {
        tot.qbIncome += qb.income; tot.qbNet += qb.net;
        const qbMargin = qb.income ? Math.round((qb.net / qb.income) * 1000) / 10 : null;
        const t = `QB period &quot;${Formatters.escapeHtml(qb.period.label)}&quot; · uploaded ${new Date(qb.period.uploadedAt).toLocaleDateString()}`;
        qbCells = `<td class="num" title="${t}" style="font-weight:600; color:${qb.net >= 0 ? 'var(--success)' : 'var(--danger)'};">${money(qb.net)}</td>` +
                  `<td class="num" title="${t}"><span class="mono num">${ProfitabilityPage.pct(qbMargin)}</span></td>`;
      }
      const future = r.month > currentMonth;
      const liveStyle = future ? ' style="color:var(--text-muted);" title="month not finished — on the books so far"' : '';
      return `
        <tr>
          <td><strong>${monthNames[r.month - 1]}</strong></td>
          <td class="num"${liveStyle}>${money(r.actual_gross)}</td>
          <td class="num"${liveStyle}>${money(r.actual_mgmt_fee)}</td>
          <td class="num" style="color:var(--text-muted);">${money(r.projected_gross)}</td>
          <td class="num" style="color:var(--text-muted);">${money(r.projected_mgmt_fee)}</td>
          ${qbCells}
        </tr>`;
    }).join('');

    const totQBMargin = tot.qbIncome ? Math.round((tot.qbNet / tot.qbIncome) * 1000) / 10 : null;
    const groupTh = 'text-align:center; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em; border-bottom:1px solid var(--border);';

    wrap.innerHTML = `
      <table id="pnl-monthly-table" style="min-width: 780px; width: 100%;">
        <thead>
          <tr>
            <th rowspan="2" style="text-align:left; vertical-align:bottom;">Month</th>
            <th colspan="2" style="${groupTh}">Live engine — synced reservations</th>
            <th colspan="2" style="${groupTh}">Projected — saved plan</th>
            <th colspan="2" style="${groupTh}">${qbHeader}</th>
          </tr>
          <tr>
            <th class="num">Gross</th>
            <th class="num">Mgmt rev</th>
            <th class="num">Proj gross</th>
            <th class="num">Proj mgmt</th>
            <th class="num" title="sum of QB property columns — before the overhead pool">Net</th>
            <th class="num" title="QB net ÷ QB gross for that month">Margin on gross</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr style="background: var(--bg-subtle); font-weight: 600;">
            <td>TOTAL</td>
            <td class="num">${money(tot.gross)}</td>
            <td class="num">${money(tot.mgmt)}</td>
            <td class="num">${money(tot.projGross)}</td>
            <td class="num">${money(tot.projMgmt)}</td>
            <td class="num" title="QB months only">${coveredMonths.length ? money(tot.qbNet) : '—'}</td>
            <td class="num" title="QB months only">${ProfitabilityPage.pct(totQBMargin)}</td>
          </tr>
          <tr>
            <td colspan="7" style="font-size:11px; color:var(--text-muted); padding:6px 12px;">
              QuickBooks columns cover <span class="mono num">${coveredMonths.length} of 12</span> months of ${year} — QB net = property classes only, before the overhead pool. Live-engine months after ${monthNames[Math.min(11, Math.max(0, currentMonth - 1))]} show what is on the books so far, not a finished month.
            </td>
          </tr>
        </tfoot>
      </table>`;
  }

  private async loadProjSummary(): Promise<void> {
    try {
      const data = await api.get<any>(`/projections/summary/${this.projYear}`);
      const monthly: any[] = data?.monthly || [];
      this.projRows = monthly.map((row: any) => ({
        month: parseInt(row.month),
        projected_gross: row.projected_gross || 0,
        projected_mgmt_fee: row.projected_mgmt_fee || 0,
        projected_net: row.projected_net || 0,
        actual_gross: row.actual_gross || 0,
        actual_mgmt_fee: row.actual_mgmt_fee || 0,
        actual_net: row.actual_net || 0,
      }));
      for (let m = 1; m <= 12; m++) {
        if (!this.projRows.find(r => r.month === m)) {
          this.projRows.push({ month: m, projected_gross: 0, projected_mgmt_fee: 0, projected_net: 0, actual_gross: 0, actual_mgmt_fee: 0, actual_net: 0 });
        }
      }
      this.projRows.sort((a, b) => a.month - b.month);
    } catch (error) {
      console.error('Failed to load projections summary:', error);
      this.projRows = [];
    }
    this.projLoaded = true;
    this.renderMonthlyStatement();
    this.renderProjEditor();
  }

  // ── 5 · Config strip — monthly projections editor ─────────────────────────

  private renderProjEditor(): void {
    const tbody = document.getElementById('pnl-proj-body');
    const tfoot = document.getElementById('pnl-proj-foot');
    if (!tbody) return;
    if (!this.projRows.length) {
      tbody.innerHTML = '<tr><td colspan="4" class="empty-state">Could not load projections.</td></tr>';
      if (tfoot) tfoot.innerHTML = '';
      return;
    }
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const input = (r: ProjRow, field: 'projected_gross' | 'projected_mgmt_fee') =>
      `<input type="number" class="pnl-proj-input mono num" data-month="${r.month}" data-field="${field}" value="${Math.round(r[field])}"
        style="width: 96px; text-align: right; padding: 4px 6px; border: 1px solid var(--border); border-radius: 6px; font-size: 12px;">`;
    tbody.innerHTML = this.projRows.map(r => `
      <tr>
        <td><strong>${monthNames[r.month - 1]}</strong></td>
        <td style="text-align:right;">${input(r, 'projected_gross')}</td>
        <td style="text-align:right;">${input(r, 'projected_mgmt_fee')}</td>
        <td class="mono num" style="text-align:right; color:var(--text-muted);">${ProfitabilityPage.money(r.projected_net)}</td>
      </tr>`).join('');
    const tot = this.projRows.reduce((a, r) => {
      a.g += r.projected_gross; a.m += r.projected_mgmt_fee; a.n += r.projected_net; return a;
    }, { g: 0, m: 0, n: 0 });
    if (tfoot) {
      tfoot.innerHTML = `
        <tr style="background: var(--bg-subtle); font-weight: 600;">
          <td>TOTAL</td>
          <td class="mono num" style="text-align:right;">${ProfitabilityPage.money(tot.g)}</td>
          <td class="mono num" style="text-align:right;">${ProfitabilityPage.money(tot.m)}</td>
          <td class="mono num" style="text-align:right;">${ProfitabilityPage.money(tot.n)}</td>
        </tr>`;
    }
  }

  private async saveProjections(): Promise<void> {
    const btn = document.getElementById('pnl-proj-save') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
    try {
      const projections = this.projRows.map(r => ({
        month: r.month,
        year: this.projYear,
        projected_gross: r.projected_gross,
        projected_mgmt_fee: r.projected_mgmt_fee,
        projected_net: r.projected_net,
        actual_gross: r.actual_gross,
        actual_mgmt_fee: r.actual_mgmt_fee,
        actual_net: r.actual_net,
      }));
      await api.post('/projections/bulk', { projections });
      if (btn) btn.textContent = 'Saved ✓';
      // Round-trip so the statement reflects what the server actually stored.
      await this.loadProjSummary();
    } catch (error) {
      console.error('Failed to save projections:', error);
      alert('Failed to save projections');
    } finally {
      if (btn) {
        btn.disabled = false;
        setTimeout(() => { if (btn) btn.textContent = 'Save all changes'; }, 1500);
      }
    }
  }

  // ── 5 · Config strip — BLR assumptions ─────────────────────────────────────

  private async loadBLR(): Promise<void> {
    try {
      this.blrFees = (await api.get<BLRFee[]>('/blr-projections')) || [];
    } catch (error) {
      console.error('Failed to load BLR projections:', error);
      this.blrFees = [];
    }
    this.renderBLRTable();
  }

  private renderBLRTable(): void {
    const tbody = document.getElementById('pnl-blr-body');
    if (!tbody) return;
    if (!this.blrFees.length) {
      tbody.innerHTML = '<tr><td colspan="4" class="empty-state">No BLR fees configured. Click "+ Add BLR fee" to add one.</td></tr>';
      return;
    }
    tbody.innerHTML = this.blrFees.map(fee => `
      <tr>
        <td>${Formatters.escapeHtml(fee.fee_name)}</td>
        <td class="mono num" style="text-align:right;">${ProfitabilityPage.money(fee.price_per_occurrence)}</td>
        <td class="mono num" style="text-align:right;">${ProfitabilityPage.pct(fee.take_up_rate, 0)}</td>
        <td style="text-align:right;"><button class="btn btn-sm btn-danger pnl-del-blr" data-id="${fee.id}">Delete</button></td>
      </tr>`).join('');
  }

  private async addBLRFee(): Promise<void> {
    const name = prompt('Fee name (e.g., "Pet Fee"):');
    if (!name) return;
    const price = parseFloat(prompt('Price per occurrence:', '0') || '0');
    const rate  = parseFloat(prompt('Take-up rate %:', '50') || '50');
    try {
      await api.post('/blr-projections', { fee_name: name, price_per_occurrence: price, take_up_rate: rate });
      await this.loadBLR();
    } catch (error) {
      console.error('Failed to add BLR fee:', error);
      alert('Failed to add BLR fee');
    }
  }

  private async deleteBLRFee(id: number): Promise<void> {
    if (!confirm('Delete this BLR projection?')) return;
    try {
      await api.delete(`/blr-projections?id=${id}`);
      await this.loadBLR();
    } catch (error) {
      console.error('Failed to delete BLR fee:', error);
    }
  }

  // ── 3 · Revenue breakdown by property (ported from PortfolioPage) ──────────
  // Engine-true per property: Gross → Commissionable → Mgmt Fee → Total
  // Company → Owner Net, with a per-property formula picker that writes a REAL
  // formula assignment (the thing the fee engine consumes).
  private async loadRevenueMetrics(): Promise<void> {
    // Window: the global month picker when set, else year-to-date.
    const g = (window as any).globalDateRange as { from: string; to: string; month?: string } | undefined;
    const y = new Date().getFullYear();
    const today = new Date().toISOString().split('T')[0];
    const from = g?.from || `${y}-01-01`;
    const to = g?.to || today;
    const windowEl = document.getElementById('pnl-revmetrics-window');
    if (windowEl) windowEl.textContent = g?.month ? `month of ${g.month}` : `${y}-01-01 → ${today} · year-to-date (pick a month above to narrow)`;
    const tbody = document.getElementById('pnl-revmetrics');
    try {
      const [res, formulasRes, assignRes] = await Promise.all([
        fetch(`/api/portfolio/revenue-metrics?start_date=${from}&end_date=${to}`),
        fetch('/api/formulas'),
        fetch('/api/formula-assignments'),
      ]);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const formulas: any[] = formulasRes.ok ? await formulasRes.json() : [];
      const assignments: any[] = assignRes.ok ? await assignRes.json() : [];
      this.assignmentByListing = new Map(assignments.filter(a => a.assignment).map(a => [String(a.listing_id), a.assignment]));
      if (!tbody) return;
      const money = (v: number) => Number.isFinite(v) ? '$' + Math.round(v).toLocaleString() : '—';
      const rows = (data.rows || []);
      if (!rows.length) { tbody.innerHTML = '<tr><td colspan="7" class="empty-state" style="padding:24px;">No revenue in range.</td></tr>'; return; }
      const formulaSelect = (r: any) => {
        const assigned = this.assignmentByListing.get(String(r.id));
        const current = assigned?.formula_code || '';
        return `<select class="rev-formula-sel input" data-id="${r.id}" style="font-size:11px; padding:4px 8px; max-width:190px; width:auto;">
            <option value="" ${current ? '' : 'selected'}>default (${Formatters.escapeHtml((r.formula_code || '20%').replace('WCH_', ''))})</option>
            ${formulas.map((f: any) => `<option value="${f.formula_code}" ${f.formula_code === current ? 'selected' : ''}>${Formatters.escapeHtml(f.formula_name || f.formula_code)}</option>`).join('')}
          </select>`;
      };
      tbody.innerHTML = rows.map((r: any) => `
        <tr>
          <td style="font-weight:500;">${Formatters.escapeHtml(r.name || r.id)}</td>
          <td>${formulaSelect(r)}</td>
          <td class="num">${money(r.gross)}</td>
          <td class="num">${money(r.commissionable)}</td>
          <td class="num" style="color:var(--warning-text);">${money(r.mgmt_fee)}</td>
          <td class="num" style="font-weight:600; color:var(--success-text);">${money(r.total_company)}</td>
          <td class="num text-muted">${money(r.owner_net)}</td>
        </tr>`).join('');
      const t = data.totals || {};
      const foot = document.getElementById('pnl-revmetrics-foot');
      if (foot) foot.innerHTML = `<tr style="background:var(--bg-subtle); font-weight:700;">
        <td>TOTAL</td><td></td>
        <td class="num">${money(t.gross)}</td>
        <td class="num">${money(t.commissionable)}</td>
        <td class="num" style="color:var(--warning-text);">${money(t.mgmt_fee)}</td>
        <td class="num" style="color:var(--success-text);">${money(t.total_company)}</td>
        <td class="num">${money(t.owner_net)}</td></tr>`;

      // Editing a property's formula creates/updates/removes its DB formula
      // assignment, then recomputes the table through the engine. Bound once
      // on the persistent tbody element (innerHTML swaps keep the listener).
      if (tbody && !(tbody as any)._formulaBound) {
        (tbody as any)._formulaBound = true;
        tbody.addEventListener('change', async (e) => {
          const sel = (e.target as HTMLElement).closest<HTMLSelectElement>('.rev-formula-sel');
          if (!sel) return;
          const prev = sel.value; sel.disabled = true;
          const lid = sel.dataset.id!;
          const todayStr = new Date().toISOString().split('T')[0];
          try {
            const existing = this.assignmentByListing.get(String(lid));
            if (sel.value) {
              const r = await fetch('/api/formula-assignments', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ listing_id: lid, formula_code: sel.value, effective_from: todayStr, notes: 'set from Money · P&L revenue breakdown' }),
              });
              if (!r.ok) throw new Error((await r.json()).error || 'assign failed');
            } else if (existing) {
              const r = await fetch(`/api/formula-assignments/${existing.assignment_id}`, { method: 'DELETE' });
              if (!r.ok) throw new Error('unassign failed');
            }
            await this.loadRevenueMetrics();
          } catch (err) {
            console.error('formula assignment failed:', err);
            alert('Could not save the formula assignment: ' + (err as Error).message);
            sel.value = prev; sel.disabled = false;
          }
        });
      }
    } catch (err) {
      console.error('revenue-metrics failed:', err);
      if (tbody) tbody.innerHTML = '<tr><td colspan="7" class="empty-state" style="padding:24px;">Failed to load revenue breakdown.</td></tr>';
    }
  }

  private renderTrendChart(): void {
    const container = document.getElementById('qb-trend-container');
    const canvas = document.getElementById('qb-trend-chart') as HTMLCanvasElement;
    if (!container || !canvas) return;

    if (this.qbPeriods.length < 2) { container.style.display = 'none'; return; }
    container.style.display = 'block';

    const labels: string[] = [];
    const margins: number[] = [];
    const netIncomes: number[] = [];

    for (const period of this.qbPeriods) {
      const comp = this.applyOverheadAllocation(period.rawProps, period.overheadItems);
      const totIncome = comp.reduce((s, p) => s + p.income, 0);
      const totNet    = comp.reduce((s, p) => s + p.netIncome, 0);
      labels.push(period.label);
      margins.push(totIncome ? Math.round((totNet / totIncome) * 1000) / 10 : 0);
      netIncomes.push(totNet);
    }

    if (this.trendChartInstance) this.trendChartInstance.destroy();
    this.trendChartInstance = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          { label: 'Profit Margin %', data: margins, borderColor: '#3b82f6',
            backgroundColor: 'rgba(59,130,246,0.1)', yAxisID: 'y1', tension: 0.3, fill: true },
          { label: 'Net Income', data: netIncomes, borderColor: '#16a34a',
            backgroundColor: 'rgba(22,163,74,0.1)', yAxisID: 'y2', tension: 0.3, fill: false },
        ],
      },
      options: {
        responsive: true,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'top' },
          tooltip: { callbacks: { label: (ctx) =>
            ctx.datasetIndex === 0
              ? ` Margin: ${ctx.raw}%`
              : ` Net Income: $${Formatters.formatNumber(ctx.raw as number)}`,
          }},
        },
        scales: {
          y1: { type: 'linear', position: 'left', ticks: { callback: (v) => `${v}%` } },
          y2: { type: 'linear', position: 'right', grid: { drawOnChartArea: false },
            ticks: { callback: (v) => '$' + Formatters.formatNumber(v as number) } },
        },
      },
    });
  }

  private renderOverheadPanel(items: QBOverheadItem[], rawProps: QBPropertyRaw[]): void {
    const panel = document.getElementById('qb-overhead-panel');
    const list  = document.getElementById('qb-overhead-items');
    if (!panel || !list) return;

    if (!items.length) { panel.style.display = 'none'; return; }
    panel.style.display = 'block';

    const totalOverhead = items.reduce((s, i) => s + i.amount, 0);
    const totalRes = rawProps.reduce((sum, p) => sum + (this.qbResCounts[this.normalizeName(p.name)] || 0), 0);
    const usingRes = this.qbResCountsLoaded && totalRes > 0;

    list.innerHTML = items.map(item => `
      <div style="display: flex; align-items: center; gap: 12px; padding: 6px 0; border-bottom: 1px solid var(--border);">
        <div style="flex: 1; font-size: 13px; font-weight: 500;">${Formatters.escapeHtml(item.rowLabel)}</div>
        <div style="font-size: 13px; color: var(--text-muted);">$${Formatters.formatNumber(item.amount)}</div>
      </div>
    `).join('') + `
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; font-weight: 700; font-size: 13px; border-top: 2px solid var(--border);">
        <div>Total Overhead Pool</div>
        <div style="color: #f59e0b;">$${Formatters.formatNumber(totalOverhead)}</div>
      </div>
      <div style="margin-top: 6px; padding: 6px 10px; border-radius: 4px; font-size: 11px;
        background: ${usingRes ? 'rgba(22,163,74,0.08)' : 'rgba(245,158,11,0.08)'};
        color: ${usingRes ? '#16a34a' : '#b45309'};">
        ${usingRes
          ? `✓ Allocated by reservation count — ${totalRes} reservations across ${rawProps.filter(p => (this.qbResCounts[this.normalizeName(p.name)] || 0) > 0).length} properties. Properties with 0 reservations get no overhead.`
          : `⚠ No properties matched yet — overhead not allocated. Click "Map Properties" to link QB names to Supabase listings.`
        }
      </div>
    `;
  }

  private async renderMappingPanel(): Promise<void> {
    const panel  = document.getElementById('qb-mapping-panel');
    const rows   = document.getElementById('qb-mapping-rows');
    const status = document.getElementById('qb-mapping-status');
    if (!panel || !rows) return;

    panel.style.display = 'block';

    // Fetch all listings if not yet loaded
    if (!this.allListings.length) {
      try {
        const r = await fetch('/api/listings');
        if (r.ok) {
          this.allListings = await r.json();
          this.listingById = new Map(this.allListings.map(l => [String(l.id), l]));
        }
      } catch (_) {}
    }

    const period = this.getActivePeriod();
    if (!period || !this.allListings.length) {
      rows.innerHTML = '<tr><td colspan="2" style="padding:12px; color:var(--text-muted);">No data available.</td></tr>';
      return;
    }

    const listingOptions = [
      '<option value="">— Not mapped —</option>',
      ...this.allListings.map(l =>
        `<option value="${l.id}">${Formatters.escapeHtml(l.short_name || l.name)}</option>`
      )
    ].join('');

    const mapped    = period.rawProps.filter(p => this.qbListingMap[this.normalizeName(p.name)] !== undefined).length;
    const unmatched = period.rawProps.filter(p => this.qbResCounts[this.normalizeName(p.name)] === undefined).length;
    if (status) status.textContent = `${mapped} manually mapped · ${period.rawProps.length - unmatched} with reservation data · ${unmatched} still unmatched`;

    rows.innerHTML = period.rawProps.map(p => {
      const qbNorm   = this.normalizeName(p.name);
      const hasCount = this.qbResCounts[qbNorm] !== undefined;
      const rowBg    = !hasCount ? 'background:#fef9c3;' : '';

      const selectHtml = `<select class="qb-map-select" data-qb-norm="${qbNorm}" style="width:100%; padding:4px 6px; border:1px solid var(--border); border-radius:4px; font-size:12px; background:white;">
        ${listingOptions}
      </select>`;

      return `<tr style="${rowBg}">
        <td style="padding:6px 12px; border-bottom:1px solid var(--border); font-weight:${hasCount ? '400' : '600'};">
          ${Formatters.escapeHtml(p.name)}${hasCount ? '' : ' ⚠'}
        </td>
        <td style="padding:6px 12px; border-bottom:1px solid var(--border);">${selectHtml}</td>
      </tr>`;
    }).join('');

    // Set saved selections
    rows.querySelectorAll<HTMLSelectElement>('.qb-map-select').forEach(sel => {
      const id = this.qbListingMap[sel.dataset.qbNorm!];
      if (id !== undefined) sel.value = String(id);
    });
  }

  private bindMappingEvents(): void {
    document.getElementById('qb-map-btn')?.addEventListener('click', () => this.renderMappingPanel());

    document.getElementById('qb-close-mapping-btn')?.addEventListener('click', () => {
      const panel = document.getElementById('qb-mapping-panel');
      if (panel) panel.style.display = 'none';
    });

    document.getElementById('qb-save-mapping-btn')?.addEventListener('click', () => {
      document.querySelectorAll<HTMLSelectElement>('.qb-map-select').forEach(sel => {
        const qbNorm = sel.dataset.qbNorm!;
        const val    = parseInt(sel.value);
        if (sel.value && !isNaN(val)) {
          this.qbListingMap[qbNorm] = val;
        } else {
          delete this.qbListingMap[qbNorm];
        }
      });
      this.saveQBListingMap();
      const panel = document.getElementById('qb-mapping-panel');
      if (panel) panel.style.display = 'none';
      this.fetchPeriodReservationCounts();
    });
  }

  private renderNotSpecified(nsProps: QBPropertyRaw[]): void {
    const wrapper = document.getElementById('qb-not-specified');
    if (!wrapper) return;

    if (!nsProps.length) { wrapper.style.display = 'none'; return; }
    wrapper.style.display = 'block';

    const countEl = document.getElementById('qb-ns-count');
    if (countEl) countEl.textContent = String(nsProps.length);

    const detail = document.getElementById('qb-ns-detail');
    if (detail) {
      detail.innerHTML = nsProps.map(p =>
        `<span style="margin-right: 12px;">• ${Formatters.escapeHtml(p.name)} (Revenue: $${Formatters.formatNumber(p.income)})</span>`
      ).join('');
    }

    const toggle = document.getElementById('qb-ns-toggle');
    if (toggle) {
      toggle.onclick = () => {
        if (!detail) return;
        const shown = detail.style.display !== 'none';
        detail.style.display = shown ? 'none' : 'block';
        toggle.textContent = shown ? 'Show' : 'Hide';
      };
    }
  }

  private populateClassDropdown(computed: QBPropertyComputed[]): void {
    const classes = [...new Set(computed.map(p => p.classGroup))].sort();
    const sel = document.getElementById('qb-class-filter') as HTMLSelectElement;
    if (sel) {
      sel.innerHTML = '<option value="">All Classes</option>' +
        classes.map(c => `<option value="${Formatters.escapeHtml(c)}">${Formatters.escapeHtml(c)}</option>`).join('');
    }
  }

  private updateQBCounts(filtered: QBPropertyComputed[], all: QBPropertyComputed[]): void {
    const fc = document.getElementById('qb-filtered-count');
    const tc = document.getElementById('qb-total-count');
    if (fc) fc.textContent = String(filtered.length);
    if (tc) tc.textContent = String(all.length);
  }

  private renderQBChart(data: QBPropertyComputed[]): void {
    const container = document.getElementById('qb-chart-container');
    const canvas = document.getElementById('qb-chart') as HTMLCanvasElement;
    if (!container || !canvas) return;

    container.style.display = data.length ? 'block' : 'none';
    if (!data.length) return;

    const sorted = [...data].sort((a, b) => b.income - a.income).slice(0, 25);
    const labels = sorted.map(p => p.name.length > 28 ? p.name.substring(0, 28) + '…' : p.name);

    if (this.qbChartInstance) this.qbChartInstance.destroy();
    this.qbChartInstance = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Cost of Service',
            data: sorted.map(p => Math.max(0, p.cogs)),
            backgroundColor: 'rgba(248,113,113,0.85)',
            borderWidth: 0,
            borderRadius: 0,
            stack: 'rev',
          },
          {
            label: 'Operating Expenses',
            data: sorted.map(p => Math.max(0, p.expenses)),
            backgroundColor: 'rgba(251,191,36,0.85)',
            borderWidth: 0,
            borderRadius: 0,
            stack: 'rev',
          },
          {
            label: 'Net Profit',
            data: sorted.map(p => Math.max(0, p.netIncome)),
            backgroundColor: 'rgba(34,197,94,0.85)',
            borderWidth: 0,
            borderRadius: 4,
            stack: 'rev',
          },
          {
            label: 'Net Loss',
            data: sorted.map(p => Math.abs(Math.min(0, p.netIncome))),
            backgroundColor: 'rgba(220,38,38,0.7)',
            borderWidth: 0,
            borderRadius: 4,
            stack: 'loss',
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
            labels: { font: { size: 11 }, boxWidth: 12, padding: 14 },
          },
          tooltip: {
            mode: 'index',
            callbacks: {
              title: ctx => sorted[ctx[0].dataIndex]?.name || '',
              afterBody: ctx => {
                const p = sorted[ctx[0].dataIndex];
                return [
                  `─────────────────`,
                  `Revenue:  $${Formatters.formatNumber(p.income)}`,
                  `Margin:   ${p.profitMargin}%`,
                  `Eff. Margin: ${p.effectiveMargin}%`,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            ticks: { callback: v => '$' + Formatters.formatNumber(v as number), font: { size: 10 } },
            grid: { color: '#f1f5f9' },
          },
          y: {
            stacked: true,
            ticks: { font: { size: 10 } },
            grid: { display: false },
          },
        },
      },
    });
  }

  private renderQBTable(data: QBPropertyComputed[]): void {
    const tableContainer = document.getElementById('qb-table-container');
    const tbody = document.getElementById('qb-table-body');
    const paginationEl = document.getElementById('qb-pagination');
    const totalsStrip  = document.getElementById('qb-totals-strip');
    if (!tableContainer || !tbody) return;

    tableContainer.style.display = data.length ? 'block' : 'none';
    if (paginationEl) paginationEl.style.display = data.length > this.qbPerPage ? 'flex' : 'none';
    if (totalsStrip)  totalsStrip.style.display  = data.length ? 'block' : 'none';

    if (!data.length) {
      tbody.innerHTML = '<tr><td colspan="12" class="empty-state">No properties match the current filters.<\/td><\/tr>';
      return;
    }

    this.container.querySelectorAll('.qb-sort').forEach(th => {
      const f = (th as HTMLElement).dataset.field;
      (th as HTMLElement).style.background = f === this.qbSortField ? 'var(--accent-soft)' : '';
    });

    const pct = (n: number) => {
      if (!Number.isFinite(n)) return `<span class="text-muted">—</span>`;
      const color = n >= 20 ? 'var(--success)' : n >= 0 ? 'var(--warning)' : 'var(--danger)';
      return `<span style="color:${color}; font-weight:600;">${n.toFixed(1)}%</span>`;
    };
    const money = (n: number) => {
      const color = n < 0 ? 'var(--danger)' : 'inherit';
      return `<span style="color:${color};">${ProfitabilityPage.money(n)}</span>`;
    };
    const CLASS_COLORS: Record<string, string> = {
      'Managed': '#3b82f6', 'Arbitrage': '#8b5cf6', 'Owned': '#16a34a', 'MMB': '#f59e0b',
    };
    const classBadge = (c: string) => {
      const bg = CLASS_COLORS[c] || '#6b7280';
      return `<span style="background:${bg}20;color:${bg};border:1px solid ${bg}40;border-radius:4px;padding:2px 7px;font-size:11px;font-weight:600;white-space:nowrap;">${Formatters.escapeHtml(c)}</span>`;
    };

    const total = data.length;
    const start = (this.qbPage - 1) * this.qbPerPage;
    const end   = Math.min(start + this.qbPerPage, total);
    const pageRows = data.slice(start, end);

    tbody.innerHTML = pageRows.map(p => {
      const qbNorm    = this.normalizeName(p.name);
      const listingId = this.qbNormToListingId[qbNorm];
      const listing   = listingId ? this.listingById.get(listingId) : null;
      const channels  = this.renderChannelLinks(listing);
      return `
      <tr>
        <td style="white-space:nowrap;"><strong>${Formatters.escapeHtml(p.name)}</strong></td>
        <td>${classBadge(p.classGroup)}</td>
        <td style="text-align:right;">${money(p.income)}</td>
        <td style="text-align:right;font-weight:600;">${money(p.netIncome)}</td>
        <td style="text-align:right;">${pct(p.profitMargin)}</td>
        <td style="text-align:right;">${pct(p.expenseRatio)}</td>
        <td style="text-align:right;">${p.reservations > 0 ? p.reservations : p.isMapped ? '0' : '<span style="color:var(--text-muted);">—</span>'}</td>
        <td style="text-align:right;">${p.damageWaiverIncome > 0 ? money(p.damageWaiverIncome) : '<span style="color:var(--text-muted);">—</span>'}</td>
        <td style="text-align:right;color:var(--warning);">${p.allocatedOverhead > 0 ? money(-p.allocatedOverhead) : '<span style="color:var(--text-muted);">—</span>'}</td>
        <td style="text-align:right;">${pct(p.effectiveMargin)}</td>
        <td style="white-space:nowrap;">${channels}</td>
      </tr>`;
    }).join('');

    // Pagination controls
    const pages = Math.ceil(total / this.qbPerPage);
    const setText = (id: string, v: string) => { const e = document.getElementById(id); if (e) e.textContent = v; };
    setText('qb-page-start', String(start + 1));
    setText('qb-page-end',   String(end));
    setText('qb-page-total', String(total));
    setText('qb-page-label', `Page ${this.qbPage} of ${pages}`);
    const prevBtn = document.getElementById('qb-prev-btn') as HTMLButtonElement | null;
    const nextBtn = document.getElementById('qb-next-btn') as HTMLButtonElement | null;
    if (prevBtn) prevBtn.disabled = this.qbPage <= 1;
    if (nextBtn) nextBtn.disabled = this.qbPage >= pages;

    // Totals strip — always reflects full filtered set
    const tot = data.reduce((acc, p) => {
      acc.income += p.income; acc.netIncome += p.netIncome; acc.expenses += p.expenses;
      acc.damageWaiverIncome += p.damageWaiverIncome; acc.allocatedOverhead += p.allocatedOverhead;
      acc.reservations += p.reservations;
      return acc;
    }, { income: 0, netIncome: 0, expenses: 0, damageWaiverIncome: 0, allocatedOverhead: 0, reservations: 0 });

    const tMargin  = tot.income ? Math.round((tot.netIncome / tot.income) * 1000) / 10 : 0;
    const tExpRatio= tot.income ? Math.round((tot.expenses  / tot.income) * 1000) / 10 : 0;
    const tEffMgn  = tot.income
      ? Math.round(((tot.netIncome - tot.allocatedOverhead) / tot.income) * 1000) / 10 : 0;
    const netColor = tot.netIncome >= 0 ? '#4ade80' : '#f87171';

    setText('qbt-count',      String(total));
    setText('qbt-income',     `$${Formatters.formatNumber(tot.income)}`);
    setText('qbt-net',        `$${Formatters.formatNumber(tot.netIncome)}`);
    setText('qbt-margin',     `${tMargin}%`);
    setText('qbt-exp-pct',    `${tExpRatio}%`);
    setText('qbt-reservations', String(tot.reservations));
    setText('qbt-dw',         `$${Formatters.formatNumber(tot.damageWaiverIncome)}`);
    setText('qbt-overhead',   tot.allocatedOverhead > 0 ? `$${Formatters.formatNumber(tot.allocatedOverhead)}` : '—');
    setText('qbt-eff-margin', `${tEffMgn}%`);
    const netEl = document.getElementById('qbt-net');
    if (netEl) netEl.style.color = netColor;
  }

  private exportQBTableCSV(): void {
    const data = this.getFilteredData();
    if (!data.length) return;

    const period = this.getActivePeriod();
    const label  = period?.label ?? 'export';

    const headers = ['Property', 'Class', 'Revenue', 'COGS', 'Gross Profit', 'Expenses', 'Net Income',
      'Profit Margin %', 'Expense Ratio %', 'Reservations', 'DW Income', 'Allocated Overhead', 'Effective Margin %'];

    const escape = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;

    const rows = data.map(p => [
      escape(p.name),
      escape(p.classGroup),
      p.income.toFixed(2),
      p.cogs.toFixed(2),
      p.grossProfit.toFixed(2),
      p.expenses.toFixed(2),
      p.netIncome.toFixed(2),
      p.profitMargin.toFixed(1),
      p.expenseRatio.toFixed(1),
      p.isMapped ? p.reservations : '',
      p.damageWaiverIncome.toFixed(2),
      p.allocatedOverhead.toFixed(2),
      p.effectiveMargin.toFixed(1),
    ].join(','));

    const csv  = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `profitability-${label.replace(/[^a-z0-9]/gi, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Live data (unchanged) ──────────────────────────────────────────────────

  private async loadData(preservePage = false): Promise<void> {
    try {
      const [data, typesResp] = await Promise.all([
        api.get<any>('/portfolio/profitability'),
        api.get<{ types: Record<string, string> }>('/listings/property-types'),
      ]);
      this.data = data;
      // Seed type map from the dedicated endpoint, then overlay each row's
      // authoritative `type` from the profitability response.
      this.propertyTypes = { ...(typesResp?.types || {}) };
      (this.data?.properties || []).forEach((p: any) => {
        if (p.id != null && p.type) this.propertyTypes[String(p.id)] = p.type;
      });
      if (!preservePage) this.leaderPage = 1;
      // Headline band falls back to this modeled block when no QB period is loaded.
      this.renderHeadline();
      this.renderTable();
    } catch (error) {
      console.error('Failed to load profitability data:', error);
      const tbody = document.getElementById('profit-table-body');
      if (tbody) tbody.innerHTML = '<tr><td colspan="5" class="empty-state">Failed to load data.<\/td><\/tr>';
    }
  }

  // ("Top Properties by GOR" chart + 6-card stats grid removed: redundant with
  //  Money's property table and the headline band / leaderboard totals here.)

  // Filter the loaded properties by the leaderboard search + type controls,
  // then apply the active sort. Pagination slices this result.
  private getFilteredLeaderRows(): any[] {
    const rows: any[] = this.data?.properties || [];
    const search = this.leaderSearch.trim().toLowerCase();
    const filtered = rows.filter(p => {
      if (search && !String(p.name || '').toLowerCase().includes(search)) return false;
      if (this.leaderType !== 'all') {
        const idStr = p.id != null ? String(p.id) : '';
        const type = this.propertyTypes[idStr] || p.type || 'standard';
        if (type !== this.leaderType) return false;
      }
      return true;
    });
    return filtered.sort((a, b) => {
      const mult = this.sortDir === 'desc' ? -1 : 1;
      return (a[this.sortField] - b[this.sortField]) * mult;
    });
  }

  private renderTable(): void {
    const tbody = document.getElementById('profit-table-body');
    const tfoot = document.getElementById('profit-table-foot');
    const controls = document.getElementById('leader-controls');
    if (!tbody || !this.data?.properties) return;

    const sorted = this.getFilteredLeaderRows();

    const total = sorted.length;
    const start = (this.leaderPage - 1) * this.leaderPerPage;
    const end   = Math.min(start + this.leaderPerPage, total);
    const pageRows = sorted.slice(start, end);

    if (controls) {
      controls.innerHTML = filterBar({
        searchId: 'leader-search',
        searchValue: this.leaderSearch,
        searchPlaceholder: 'Search properties…',
        selects: [{
          id: 'leader-type-filter',
          value: this.leaderType,
          options: [
            { value: 'all', label: 'All types' },
            ...PROPERTY_TYPE_META.map(m => ({ value: m.value, label: m.label })),
          ],
        }],
        countLabel: `${total} propert${total === 1 ? 'y' : 'ies'}`,
      });
    }

    if (!total) {
      tbody.innerHTML = '<tr><td colspan="5" class="empty-state">No properties match the current filters.<\/td><\/tr>';
      if (tfoot) tfoot.innerHTML = '';
      this.renderLeaderPager(total);
      this.bindLeaderControls();
      return;
    }

    tbody.innerHTML = pageRows.map((p, i) => {
      const idStr      = p.id != null ? String(p.id) : '';
      const type       = this.propertyTypes[idStr] || p.type || 'standard';
      const meta       = typeMeta(type);
      const isExpanded = idStr !== '' && idStr === this.expandedRowId;
      const typePill   = `<span style="display:inline-block;margin-top:3px;background:${meta.color}1a;color:${meta.color};border:1px solid ${meta.color}40;border-radius:4px;padding:1px 5px;font-size:9px;font-weight:600;white-space:nowrap;">${meta.label}</span>`;
      const marginNum = Number.isFinite(p.margin_percent) ? p.margin_percent : 0;
      const row = `
      <tr class="leader-row" data-id="${idStr}" style="cursor:${idStr ? 'pointer' : 'default'};${isExpanded ? 'background:var(--accent-soft);' : ''}">
        <td style="color:var(--text-muted); text-align:center; font-size:11px;">${start + i + 1}</td>
        <td style="max-width:130px; font-size:12px;" title="${Formatters.escapeHtml(p.name)}">
          <div class="u-truncate"><strong>${Formatters.escapeHtml(p.name)}</strong></div>
          ${typePill}
        </td>
        <td style="font-size:12px;">${ProfitabilityPage.money(p.gor)}</td>
        <td style="font-size:12px; color:${p.net_profit >= 0 ? 'var(--success)' : 'var(--danger)'};">
          <strong>${ProfitabilityPage.money(p.net_profit)}</strong>
        </td>
        <td style="font-size:12px;">
          <div style="display:flex; align-items:center; gap:6px;">
            <div style="background:var(--border); border-radius:3px; height:6px; width:40px; overflow:hidden; flex-shrink:0;">
              <div style="background:${marginNum >= 25 ? 'var(--success)' : marginNum >= 15 ? 'var(--warning)' : 'var(--danger)'}; height:100%; width:${Math.max(0, Math.min(marginNum, 100))}%;"></div>
            </div>
            ${ProfitabilityPage.pct(p.margin_percent)}
          </div>
        </td>
      </tr>`;
      const detail = isExpanded ? `
      <tr class="leader-detail" data-id="${idStr}">
        <td colspan="5" style="padding:0; background:var(--bg-secondary, #f8fafc); border-top:1px solid var(--border);">
          ${this.renderLeaderDetail(p, type)}
        </td>
      </tr>` : '';
      return row + detail;
    }).join('');

    if (tfoot && this.data.portfolio) {
      const p = this.data.portfolio;
      tfoot.innerHTML = `
        <tr style="background:var(--bg-subtle); font-weight:600; font-size:12px;">
          <td colspan="2">Total — ${total}${p.total_count ? ` of ${p.total_count}` : ''}${p.excluded_count ? ` (${p.excluded_count} excluded)` : ''}</td>
          <td>${ProfitabilityPage.money(p.gor)}</td>
          <td style="color:var(--success);">${ProfitabilityPage.money(p.net_profit)}</td>
          <td>${ProfitabilityPage.pct(p.margin_percent)}</td>
        </tr>
      `;
    }

    this.renderLeaderPager(total);
    this.bindLeaderControls();
  }

  // Update the existing leaderboard pagination strip for the given filtered total.
  private renderLeaderPager(total: number): void {
    const start = (this.leaderPage - 1) * this.leaderPerPage;
    const end   = Math.min(start + this.leaderPerPage, total);
    const pages = Math.max(1, Math.ceil(total / this.leaderPerPage));
    const setText = (id: string, v: string) => { const e = document.getElementById(id); if (e) e.textContent = v; };
    setText('leader-page-start', String(total === 0 ? 0 : start + 1));
    setText('leader-page-end',   String(end));
    setText('leader-page-total', String(total));
    setText('leader-page-label', total > 0 ? `Page ${this.leaderPage} of ${pages}` : '');
    const prevBtn = document.getElementById('leader-prev-btn') as HTMLButtonElement | null;
    const nextBtn = document.getElementById('leader-next-btn') as HTMLButtonElement | null;
    if (prevBtn) prevBtn.disabled = this.leaderPage <= 1;
    if (nextBtn) nextBtn.disabled = this.leaderPage >= pages || total === 0;
  }

  // Wire the leaderboard search + type filter (re-bound on each render since the
  // filterBar markup is regenerated). Resets to page 1 and refocuses search.
  private bindLeaderControls(): void {
    const search = document.getElementById('leader-search') as HTMLInputElement | null;
    if (search) {
      search.oninput = () => {
        this.leaderSearch = search.value;
        this.leaderPage = 1;
        this.renderTable();
        const next = document.getElementById('leader-search') as HTMLInputElement | null;
        if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
      };
    }
    const typeSel = document.getElementById('leader-type-filter') as HTMLSelectElement | null;
    if (typeSel) {
      typeSel.onchange = () => {
        this.leaderType = typeSel.value;
        this.leaderPage = 1;
        this.renderTable();
      };
    }
  }

  // ── Leaderboard detail drawer + property type ────────────────────────────────

  private renderLeaderDetail(p: any, type: string): string {
    const idStr = p.id != null ? String(p.id) : '';
    const meta  = typeMeta(type);
    const money = (n: number, color?: string) =>
      `<span style="color:${color || 'inherit'};">${ProfitabilityPage.money(n)}</span>`;

    const stat = (label: string, value: string) => `
      <div style="flex:1; min-width:90px;">
        <div style="font-size:10px; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted);">${label}</div>
        <div style="font-size:14px; font-weight:600; margin-top:2px;">${value}</div>
      </div>`;

    const options = PROPERTY_TYPE_META.map(m =>
      `<option value="${m.value}"${m.value === type ? ' selected' : ''}>${m.label}</option>`
    ).join('');

    return `
      <div style="padding:14px 16px;">
        <div style="display:flex; flex-wrap:wrap; gap:14px; margin-bottom:12px;">
          ${stat('GOR', money(p.gor))}
          ${stat('MOR', money(p.mor))}
          ${stat('Est. Expenses (modeled)', money(p.estimated_expenses, 'var(--danger)'))}
          ${stat('Net Profit (modeled)', money(p.net_profit, p.net_profit >= 0 ? 'var(--success)' : 'var(--danger)'))}
          ${stat('Margin on mgmt rev (modeled)', ProfitabilityPage.pct(p.margin_percent))}
        </div>
        <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
          <label style="font-size:11px; font-weight:600; color:var(--text-muted);">Property type</label>
          <select class="leader-type-select" data-id="${idStr}"${idStr ? '' : ' disabled'}
            style="padding:4px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px; background:white;">
            ${options}
          </select>
          <span class="leader-type-status" data-id="${idStr}" style="font-size:11px; color:var(--text-muted);">${Formatters.escapeHtml(meta.note)}</span>
        </div>
        <div style="margin-top:8px; font-size:10px; color:var(--text-muted); line-height:1.5;">
          <strong>Inactive</strong> properties are excluded from margin analysis. <strong>MTR</strong> and <strong>Owner-occupied</strong> units get reduced overhead. Changes apply on the next data refresh.
        </div>
      </div>`;
  }

  private async savePropertyType(id: string, type: string): Promise<void> {
    const statusEl = this.container.querySelector(`.leader-type-status[data-id="${id}"]`) as HTMLElement | null;
    if (statusEl) { statusEl.textContent = 'Saving…'; statusEl.style.color = 'var(--text-muted)'; }
    try {
      await api.post('/listings/property-type', { id, type });
      this.propertyTypes[id] = type;
      // If the property was set to 'inactive' it drops out of the response, so
      // collapse the drawer to avoid pointing at a now-missing row.
      if (type === 'inactive') this.expandedRowId = null;
      // Refresh the underlying analysis so margins reflect the new type.
      await this.loadData(true);
    } catch (e) {
      if (statusEl) { statusEl.textContent = 'Failed to save'; statusEl.style.color = '#dc2626'; }
    }
  }

  // ── Events ─────────────────────────────────────────────────────────────────

  private bindEvents(): void {
    // Live leaderboard sort
    this.container.querySelectorAll('.sortable').forEach(th => {
      th.addEventListener('click', () => {
        const field = (th as HTMLElement).dataset.field || 'gor';
        if (this.sortField === field) this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
        else { this.sortField = field; this.sortDir = 'desc'; }
        this.leaderPage = 1;
        this.renderTable();
      });
    });

    // Leaderboard row click → toggle detail drawer (delegated; tbody persists across renders)
    const leaderBody = document.getElementById('profit-table-body');
    leaderBody?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      // Ignore clicks originating inside the detail drawer (e.g. the select)
      if (target.closest('.leader-detail')) return;
      const row = target.closest('.leader-row') as HTMLElement | null;
      if (!row) return;
      const id = row.dataset.id;
      if (!id) return;
      this.expandedRowId = this.expandedRowId === id ? null : id;
      this.renderTable();
    });

    // Property type change inside an expanded drawer
    leaderBody?.addEventListener('change', (e) => {
      const sel = (e.target as HTMLElement).closest('.leader-type-select') as HTMLSelectElement | null;
      if (!sel || !sel.dataset.id) return;
      this.savePropertyType(sel.dataset.id, sel.value);
    });

    document.getElementById('leader-prev-btn')?.addEventListener('click', () => {
      if (this.leaderPage > 1) { this.leaderPage--; this.renderTable(); }
    });
    document.getElementById('leader-next-btn')?.addEventListener('click', () => {
      const pages = Math.ceil(this.getFilteredLeaderRows().length / this.leaderPerPage);
      if (this.leaderPage < pages) { this.leaderPage++; this.renderTable(); }
    });

    // QuickBooks workbench <details> — Chart.js canvases sized while hidden
    // come up blank, so re-render the QB view whenever the panel opens.
    const workbench = document.getElementById('qb-workbench') as HTMLDetailsElement | null;
    workbench?.addEventListener('toggle', () => {
      if (workbench.open) this.refreshQBView(false);
    });

    // Config strip: monthly projections editor (delegated — tbody persists)
    document.getElementById('pnl-proj-save')?.addEventListener('click', () => this.saveProjections());
    document.getElementById('pnl-proj-body')?.addEventListener('change', (e) => {
      const input = (e.target as HTMLElement).closest<HTMLInputElement>('.pnl-proj-input');
      if (!input) return;
      const month = parseInt(input.dataset.month || '0');
      const field = input.dataset.field as 'projected_gross' | 'projected_mgmt_fee' | undefined;
      const row = this.projRows.find(r => r.month === month);
      if (!row || !field) return;
      row[field] = parseFloat(input.value) || 0;
      this.renderMonthlyStatement(); // projected columns above react immediately
      this.renderProjEditor();       // totals row refresh
    });

    // Config strip: BLR add/delete (delegated)
    document.getElementById('pnl-add-blr')?.addEventListener('click', () => this.addBLRFee());
    document.getElementById('pnl-blr-body')?.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('.pnl-del-blr');
      if (btn) this.deleteBLRFee(parseInt(btn.dataset.id || '0'));
    });

    // QB table sort
    this.container.querySelectorAll('.qb-sort').forEach(th => {
      th.addEventListener('click', () => {
        const field = (th as HTMLElement).dataset.field as keyof QBPropertyComputed;
        if (!field) return;
        if (this.qbSortField === field) this.qbSortDir = this.qbSortDir === 'desc' ? 'asc' : 'desc';
        else { this.qbSortField = field; this.qbSortDir = 'desc'; }
        this.refreshQBView();
      });
    });

    // QB pagination
    document.getElementById('qb-prev-btn')?.addEventListener('click', () => {
      if (this.qbPage > 1) { this.qbPage--; this.refreshQBView(false); }
    });
    document.getElementById('qb-next-btn')?.addEventListener('click', () => {
      const pages = Math.ceil(this.getFilteredData().length / this.qbPerPage);
      if (this.qbPage < pages) { this.qbPage++; this.refreshQBView(false); }
    });

    // Export CSV
    document.getElementById('qb-export-btn')?.addEventListener('click', () => this.exportQBTableCSV());

    // Clear all periods
    document.getElementById('qb-clear-btn')?.addEventListener('click', () => {
      if (!confirm('Remove all QB periods?')) return;
      this.qbPeriods = [];
      this.qbActivePeriodIdx = 0;
      this.saveQBToStorage();
      this.renderPeriodChips();
      this.refreshQBView();
    });

    // File upload
    const fileInput = document.getElementById('qb-csv-input') as HTMLInputElement;
    const errorEl   = document.getElementById('qb-error');

    if (fileInput) {
      fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        if (errorEl) errorEl.style.display = 'none';

        const autoLabel = file.name.replace(/\.csv$/i, '').replace(/_/g, ' ');
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text = e.target?.result as string;
            const periodData = this.parseQBCSV(text);
            const newPeriod: QBPeriod = {
              id: Date.now().toString(),
              label: autoLabel,
              uploadedAt: new Date().toISOString(),
              ...periodData,
            };

            this.qbPeriods.push(newPeriod);
            if (this.qbPeriods.length > 12) this.qbPeriods = this.qbPeriods.slice(-12);
            this.qbActivePeriodIdx = this.qbPeriods.length - 1;

            this.qbSearch = ''; this.qbFilterClass = ''; this.qbFilterProfit = 'all';
            this.qbMinRevenue = 0; this.qbSortField = 'netIncome'; this.qbSortDir = 'desc';

            this.qbResCounts = {};
            this.qbResCountsLoaded = false;
            this.saveQBToStorage();
            this.renderPeriodChips();
            this.bindQBFilterEvents();
            this.refreshQBView();
            this.fetchPeriodReservationCounts();
            fileInput.value = '';
          } catch (err: any) {
            if (errorEl) { errorEl.textContent = `Parse error: ${err.message}`; errorEl.style.display = 'block'; }
          }
        };
        reader.readAsText(file);
      });
    }

    this.bindQBFilterEvents();
    this.bindMappingEvents();
  }

  private bindQBFilterEvents(): void {
    const searchEl = document.getElementById('qb-search') as HTMLInputElement;
    const classEl  = document.getElementById('qb-class-filter') as HTMLSelectElement;
    const minRevEl = document.getElementById('qb-min-revenue') as HTMLInputElement;

    if (searchEl) {
      searchEl.value = this.qbSearch;
      searchEl.oninput = () => { this.qbSearch = searchEl.value; this.refreshQBView(); };
    }
    if (classEl) {
      classEl.value = this.qbFilterClass;
      classEl.onchange = () => { this.qbFilterClass = classEl.value; this.refreshQBView(); };
    }
    if (minRevEl) {
      minRevEl.value = this.qbMinRevenue ? String(this.qbMinRevenue) : '';
      minRevEl.oninput = () => { this.qbMinRevenue = parseFloat(minRevEl.value) || 0; this.refreshQBView(); };
    }

    this.container.querySelectorAll('.qb-profit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this.qbFilterProfit = (btn as HTMLElement).dataset.val || 'all';
        this.container.querySelectorAll('.qb-profit-btn').forEach(b => {
          b.className = 'qb-profit-btn btn btn-sm btn-secondary';
        });
        btn.className = 'qb-profit-btn btn btn-sm btn-primary';
        this.refreshQBView();
      });
    });
  }

  private renderChannelLinks(listing: any): string {
    if (!listing) return '<span style="color:var(--text-muted);font-size:12px;">—</span>';
    const links: string[] = [];
    if (listing.wch_url) links.push(
      `<a href="${Formatters.escapeHtml(listing.wch_url)}" target="_blank" rel="noopener"
        style="display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:5px;font-size:10px;font-weight:600;text-decoration:none;background:#6366f11a;color:#6366f1;border:1px solid #6366f140;">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>WCH
      </a>`);
    if (listing.airbnb_url) links.push(
      `<a href="${Formatters.escapeHtml(listing.airbnb_url)}" target="_blank" rel="noopener"
        style="display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:5px;font-size:10px;font-weight:600;text-decoration:none;background:#ff5a5f1a;color:#ff5a5f;border:1px solid #ff5a5f40;">
        <svg width="9" height="9" viewBox="0 0 32 32" fill="currentColor"><path d="M16 1C7.163 1 0 8.163 0 17c0 5.356 2.63 10.09 6.668 13.02L16 31l9.332-0.98C29.37 27.09 32 22.356 32 17 32 8.163 24.837 1 16 1zm0 27.5c-2.348 0-4.246-1.9-4.246-4.247S13.652 19.007 16 19.007s4.246 1.898 4.246 4.246S18.348 28.5 16 28.5zm9.004-14.627c-0.47 1.197-1.173 2.257-2.068 3.12-0.604 0.578-1.284 1.077-2.015 1.472l-1.82-4.642c-0.28-0.715-0.973-1.18-1.74-1.15h-2.72c-0.768-0.03-1.46 0.435-1.74 1.15l-1.82 4.642c-0.73-0.395-1.41-0.894-2.015-1.472-0.895-0.863-1.598-1.923-2.068-3.12-0.235-0.598-0.35-1.24-0.338-1.887 0.052-2.56 2.135-4.6 4.696-4.6 1.032 0 1.988 0.33 2.766 0.89l1.04 0.752 1.04-0.752c0.778-0.56 1.734-0.89 2.766-0.89 2.56 0 4.644 2.04 4.696 4.6 0.012 0.647-0.103 1.289-0.338 1.887z"/></svg>Airbnb
      </a>`);
    if (listing.vrbo_url) links.push(
      `<a href="${Formatters.escapeHtml(listing.vrbo_url)}" target="_blank" rel="noopener"
        style="display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:5px;font-size:10px;font-weight:600;text-decoration:none;background:#1e5c9b1a;color:#1e5c9b;border:1px solid #1e5c9b40;">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5M2 17l10 5 10-5M2 12l10 5 10-5"/></svg>VRBO
      </a>`);
    if (listing.expedia_url) links.push(
      `<a href="${Formatters.escapeHtml(listing.expedia_url)}" target="_blank" rel="noopener"
        style="display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:5px;font-size:10px;font-weight:600;text-decoration:none;background:#f9b1281a;color:#c68b00;border:1px solid #f9b12840;">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/></svg>Expedia
      </a>`);
    if (listing.google_vr_url) links.push(
      `<a href="${Formatters.escapeHtml(listing.google_vr_url)}" target="_blank" rel="noopener"
        style="display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:5px;font-size:10px;font-weight:600;text-decoration:none;background:#4285f41a;color:#4285f4;border:1px solid #4285f440;">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 1 1 0-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0 0 12.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/></svg>Google
      </a>`);
    return links.length
      ? `<div style="display:flex;flex-wrap:wrap;gap:3px;">${links.join('')}</div>`
      : '<span style="color:var(--text-muted);font-size:12px;">—</span>';
  }
}
